import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Box, Typography, Paper, Button, TextField, Select, MenuItem, FormControl,
  InputLabel, Chip, Checkbox, IconButton, Tooltip, Grid,
  CircularProgress, Alert, LinearProgress, Divider,
  Card, CardContent, Dialog, DialogTitle, DialogContent, DialogActions,
  Snackbar, Switch, FormControlLabel, Tabs, Tab, Pagination, Menu, ListItemIcon, ListItemText
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { useDropzone } from 'react-dropzone';
import { FaImage, FaPlus, FaDownload, FaSearch, FaTimes, FaChair, FaBorderAll, FaEdit, FaTrash, FaChevronLeft, FaChevronRight, FaFileImage, FaGlobe, FaLock, FaUser, FaHistory, FaMagic, FaBoxOpen, FaImages, FaThLarge, FaColumns, FaStop, FaRedo, FaGripVertical, FaChevronDown, FaChevronUp, FaBookmark, FaFolder, FaFolderOpen, FaCopy, FaCheckCircle, FaExclamationCircle, FaLayerGroup } from 'react-icons/fa';
import { MdAutoAwesome, MdFolderSpecial, MdCompare, MdAddCircleOutline } from 'react-icons/md';
import API_BASE_URL, { API_ENDPOINTS, apiCall } from '../config/api';
import { TOOL_COLORS } from '../constants/colors';
import InterfaceHeader from '../components/InterfaceHeader';

const AI_COLOR = TOOL_COLORS.AI.primary;

// Compact label + color for the Gemini image-model chip shown in History.
// Rows generated before the model column shipped have model === null.
const modelDisplayLabel = (model) => {
  if (!model) return 'Unknown model';
  // Stable ids since 2026-10-10; older rows carry the -preview ids they ran with.
  if (model === 'gemini-3-pro-image' || model === 'gemini-3-pro-image-preview')         return 'Pro';
  if (model === 'gemini-3.1-flash-image' || model === 'gemini-3.1-flash-image-preview') return 'Flash';
  if (model === 'gemini-nano-banana-2.1')          return 'Nano Banana 2.1';
  return model;
};
const modelDisplayColor = (model) => {
  if (!model)                                       return '#94A3B8'; // slate (unknown)
  if (model === 'gemini-3-pro-image' || model === 'gemini-3-pro-image-preview')         return '#D97706'; // amber (premium tier)
  if (model === 'gemini-3.1-flash-image' || model === 'gemini-3.1-flash-image-preview') return '#0EA5E9'; // sky (fast/cheap)
  if (model === 'gemini-nano-banana-2.1')           return '#16A34A'; // green (newest, cheapest)
  return '#475569';
};

const ASPECT_RATIOS = [
  { value: '1:1', label: '1:1 (Square)' },
  { value: '9:16', label: '9:16 (Portrait Tall)' },
  { value: '3:4', label: '3:4 (Portrait)' },
  { value: '16:9', label: '16:9 (Landscape Wide)' },
  { value: '4:3', label: '4:3 (Landscape)' },
];

const RESOLUTIONS = [
  { value: '1K', label: '1K' },
  { value: '2K', label: '2K' },
  { value: '4K', label: '4K' },
];

const AIImageGenerator = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // Main tab: 'generator', 'history', or 'collections'
  const [mainTab, setMainTab] = useState('generator');

  // State
  const [mode, setMode] = useState('Furniture');
  const [imageMode, setImageMode] = useState('pairs'); // 'pairs' or 'silo'
  const [prompts, setPrompts] = useState([]);
  const [promptsLoading, setPromptsLoading] = useState(true);
  const [promptFilter, setPromptFilter] = useState('all'); // 'all', 'mine', 'public'
  const [selectedPrompts, setSelectedPrompts] = useState(new Set());
  const [searchText, setSearchText] = useState('');
  const [productImages, setProductImages] = useState([]);
  const [fixedImages, setFixedImages] = useState([]);
  const [aspectRatio, setAspectRatio] = useState('1:1');
  const [resolution, setResolution] = useState('4K');
  const [repeatCount, setRepeatCount] = useState(1);
  // Gemini image model. Nano Banana 2.1 = newest stable Flash-tier model,
  // cheapest per image (default, Klevi 2026-10-12). Pro = higher fidelity,
  // slower, more expensive. Flash = cheaper + faster, lower fidelity. Backend
  // whitelists these three strings so a typo can't redirect us at an arbitrary
  // Google endpoint.
  const [imageModel, setImageModel] = useState('gemini-nano-banana-2.1');
  const [additionalInstructions, setAdditionalInstructions] = useState('');
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [generatedImages, setGeneratedImages] = useState([]);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  // Dialog state
  const [promptDialogOpen, setPromptDialogOpen] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState(null);
  const [promptForm, setPromptForm] = useState({ name: '', text: '', isPublic: false });

  // Result view mode: 'grid' or 'comparison'
  const [resultViewMode, setResultViewMode] = useState('grid');
  const [regenSelectMode, setRegenSelectMode] = useState(false);
  const [regenSelected, setRegenSelected] = useState(new Set());
  const [currentBatchId, setCurrentBatchId] = useState(null);
  const [cropMenuAnchor, setCropMenuAnchor] = useState(null);
  const [cropMenuImage, setCropMenuImage] = useState(null);
  const [cropMenuFilename, setCropMenuFilename] = useState('');
  const [resizeDialogOpen, setResizeDialogOpen] = useState(false);
  const [resizeImage, setResizeImage] = useState(null);
  const [resizeFilename, setResizeFilename] = useState('');
  const [customWidth, setCustomWidth] = useState('');
  const [customHeight, setCustomHeight] = useState('');
  const [keepAspectRatio, setKeepAspectRatio] = useState(true);
  const [resizeOriginalDims, setResizeOriginalDims] = useState({ w: 0, h: 0 });
  const [backgroundMode, setBackgroundMode] = useState(false);
  const [batchMode, setBatchMode] = useState(false);
  // Naming mode: false (default) = "{name1}+{name2}_YYYYMMDD_HHMMSS" — descriptive,
  // sortable, never collides. true = use the product image's filename verbatim
  // (for pairs, prefer the non-detail name). Persisted in localStorage so
  // users don't have to re-toggle every visit. Key is versioned so we can
  // force a reset to "off" by bumping the suffix.
  const [simpleNaming, setSimpleNaming] = useState(() => localStorage.getItem('aiImg_simpleNaming_v2') === '1');
  useEffect(() => {
    localStorage.setItem('aiImg_simpleNaming_v2', simpleNaming ? '1' : '0');
    // Clean up the old key if present (introduced in earlier deploy).
    localStorage.removeItem('aiImg_simpleNaming');
  }, [simpleNaming]);
  const [batchJobs, setBatchJobs] = useState([]);
  const [batchJobsLoading, setBatchJobsLoading] = useState(false);
  const [batchSubmitting, setBatchSubmitting] = useState(false);
  const [batchLog, setBatchLog] = useState([]);
  const [suggestedPrompts, setSuggestedPrompts] = useState([]);
  const [suggestingPrompts, setSuggestingPrompts] = useState(false);
  const [aiGeneratedPrompts, setAiGeneratedPrompts] = useState([]);
  const [generatingAiPrompts, setGeneratingAiPrompts] = useState(false);
  const [aiPromptsDialogOpen, setAiPromptsDialogOpen] = useState(false);

  // Abort controller for cancelling generation
  const abortControllerRef = useRef(null);

  // Drag-to-reorder state for product images
  const dragIndexRef = useRef(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  // Expanded prompt text in history
  const [expandedPromptId, setExpandedPromptId] = useState(null);

  // Image preview lightbox state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [previewImages, setPreviewImages] = useState([]);
  const [zoomActive, setZoomActive] = useState(false);

  // History state
  const [historyBatches, setHistoryBatches] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotalPages, setHistoryTotalPages] = useState(1);
  const [batchJobsCollapsed, setBatchJobsCollapsed] = useState(false);
  const [expandedBatch, setExpandedBatch] = useState(null);
  const [batchImages, setBatchImages] = useState([]);
  const [batchImagesLoading, setBatchImagesLoading] = useState(false);

  const [batchInputImages, setBatchInputImages] = useState({});
  const [compareDialogBatch, setCompareDialogBatch] = useState(null);
  const [compareIndex, setCompareIndex] = useState(0);
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState(null);

  // Collections state
  const [collections, setCollections] = useState(() => JSON.parse(localStorage.getItem('ai_image_collections') || '[]'));
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [newCollectionName, setNewCollectionName] = useState('');
  const [showCreateCollection, setShowCreateCollection] = useState(false);
  const [editingCollectionId, setEditingCollectionId] = useState(null);
  const [editingCollectionName, setEditingCollectionName] = useState('');
  const [collectionCompareMode, setCollectionCompareMode] = useState(false);
  const [collectionCompareSelected, setCollectionCompareSelected] = useState(new Set());
  const [comparisonDialogOpen, setComparisonDialogOpen] = useState(false);

  // Delete collection confirmation
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  // Server sync for collections
  const collectionSaveTimerRef = useRef(null);
  const collectionsLoadedFromServer = useRef(false);

  // Save to collection menu
  const [saveMenuAnchor, setSaveMenuAnchor] = useState(null);
  const [saveMenuImageData, setSaveMenuImageData] = useState(null);

  // Add images to collection picker
  const [addImagesDialogOpen, setAddImagesDialogOpen] = useState(false);
  const [addImagesBatches, setAddImagesBatches] = useState([]);
  const [addImagesLoading, setAddImagesLoading] = useState(false);
  const [addImagesExpanded, setAddImagesExpanded] = useState(null);
  const [addImagesBatchImages, setAddImagesBatchImages] = useState([]);
  const [addImagesBatchLoading, setAddImagesBatchLoading] = useState(false);
  const [addImagesPage, setAddImagesPage] = useState(1);
  const [addImagesTotalPages, setAddImagesTotalPages] = useState(1);

  // Save collections to localStorage whenever they change + debounced server sync
  useEffect(() => {
    localStorage.setItem('ai_image_collections', JSON.stringify(collections));
    // Skip server save until initial load from server is complete
    if (!collectionsLoadedFromServer.current) return;
    // Debounce server save by 2 seconds
    if (collectionSaveTimerRef.current) clearTimeout(collectionSaveTimerRef.current);
    collectionSaveTimerRef.current = setTimeout(() => {
      apiCall(API_ENDPOINTS.AI_IMAGE_COLLECTIONS, {
        method: 'POST',
        body: JSON.stringify({ collections })
      }).catch(err => console.warn('Failed to sync collections to server:', err));
    }, 2000);
  }, [collections]);

  // Load collections from server on mount (server-first, localStorage fallback)
  useEffect(() => {
    const loadCollectionsFromServer = async () => {
      try {
        const data = await apiCall(API_ENDPOINTS.AI_IMAGE_COLLECTIONS);
        if (data.success && data.collections && data.collections.length > 0) {
          setCollections(data.collections);
        } else {
          // Server empty — check if localStorage has data to migrate
          const local = JSON.parse(localStorage.getItem('ai_image_collections') || '[]');
          if (local.length > 0) {
            // One-time migration: push localStorage collections to server
            await apiCall(API_ENDPOINTS.AI_IMAGE_COLLECTIONS, {
              method: 'POST',
              body: JSON.stringify({ collections: local })
            }).catch(err => console.warn('Failed to migrate collections to server:', err));
          }
        }
      } catch (err) {
        console.warn('Failed to load collections from server, using localStorage fallback:', err);
      } finally {
        collectionsLoadedFromServer.current = true;
      }
    };
    loadCollectionsFromServer();
  }, []); // eslint-disable-line

  // Collection CRUD actions
  const createCollection = (name) => {
    if (!name.trim()) return;
    const newCol = {
      id: Date.now().toString(),
      name: name.trim(),
      createdAt: new Date().toISOString(),
      images: [],
    };
    setCollections(prev => [...prev, newCol]);
    setSelectedCollection(newCol.id);
    setNewCollectionName('');
    setShowCreateCollection(false);
    setSnackbar({ open: true, message: `Collection "${name.trim()}" created!` });
  };

  const addToCollection = (collectionId, imageData) => {
    setCollections(prev => prev.map(col => {
      if (col.id !== collectionId) return col;
      // Skip if already exists by s3Url
      if (imageData.s3Url && col.images.some(img => img.s3Url === imageData.s3Url)) {
        return col;
      }
      return {
        ...col,
        images: [...col.images, {
          id: Date.now().toString() + '_' + Math.random().toString(36).substr(2, 6),
          batchId: imageData.batchId || null,
          promptName: imageData.promptName || 'Untitled',
          promptText: imageData.promptText || imageData.text || '',
          s3Url: imageData.s3Url || null,
          imageSrc: imageData.imageSrc || imageData.image || null,
          fileName: imageData.fileName || imageData.promptName || 'image',
          addedAt: new Date().toISOString(),
        }],
      };
    }));
    setSaveMenuAnchor(null);
    setSaveMenuImageData(null);
    const colName = collections.find(c => c.id === collectionId)?.name || 'collection';
    setSnackbar({ open: true, message: `Image saved to "${colName}"` });
  };

  const removeFromCollection = (collectionId, imageIndex) => {
    setCollections(prev => prev.map(col => {
      if (col.id !== collectionId) return col;
      return { ...col, images: col.images.filter((_, i) => i !== imageIndex) };
    }));
  };

  const confirmDeleteCollection = (collectionId) => {
    setDeleteConfirmId(collectionId);
    setDeleteConfirmOpen(true);
  };

  const deleteCollection = (collectionId) => {
    setCollections(prev => prev.filter(c => c.id !== collectionId));
    if (selectedCollection === collectionId) {
      setSelectedCollection(null);
    }
    setDeleteConfirmOpen(false);
    setDeleteConfirmId(null);
    setSnackbar({ open: true, message: 'Collection deleted.' });
  };

  const renameCollection = (collectionId, newName) => {
    if (!newName.trim()) return;
    setCollections(prev => prev.map(col =>
      col.id === collectionId ? { ...col, name: newName.trim() } : col
    ));
    setEditingCollectionId(null);
    setEditingCollectionName('');
  };

  // Add Images dialog functions
  const openAddImagesDialog = async () => {
    setAddImagesDialogOpen(true);
    setAddImagesExpanded(null);
    setAddImagesBatchImages([]);
    setAddImagesPage(1);
    await fetchAddImagesBatches(1);
  };

  const fetchAddImagesBatches = async (page) => {
    setAddImagesLoading(true);
    try {
      const data = await apiCall(API_ENDPOINTS.AI_IMAGE_HISTORY(page, 12));
      setAddImagesBatches(data.batches || []);
      setAddImagesTotalPages(data.totalPages || 1);
      setAddImagesPage(page);
    } catch (err) {
      console.error('Error fetching batches for add dialog:', err);
    } finally {
      setAddImagesLoading(false);
    }
  };

  const fetchAddImagesBatchDetail = async (batchId) => {
    if (addImagesExpanded === batchId) {
      setAddImagesExpanded(null);
      setAddImagesBatchImages([]);
      return;
    }
    setAddImagesBatchLoading(true);
    setAddImagesExpanded(batchId);
    try {
      const data = await apiCall(API_ENDPOINTS.AI_IMAGE_BATCH(batchId));
      setAddImagesBatchImages(data.images || []);
    } catch (err) {
      console.error('Error fetching batch images:', err);
    } finally {
      setAddImagesBatchLoading(false);
    }
  };

  const addImageFromPicker = (img) => {
    if (!selectedCollection) return;
    addToCollection(selectedCollection, {
      id: img.id,
      batchId: addImagesExpanded,
      promptName: img.promptName || '',
      promptText: img.promptText || '',
      s3Url: img.s3Url,
      fileName: img.fileName || '',
    });
  };

  // Open save-to-collection menu
  const handleOpenSaveMenu = (event, imageData) => {
    event.stopPropagation();
    setSaveMenuAnchor(event.currentTarget);
    setSaveMenuImageData(imageData);
  };

  const handleCloseSaveMenu = () => {
    setSaveMenuAnchor(null);
    setSaveMenuImageData(null);
  };

  // Fetch prompts from API
  const fetchPrompts = useCallback(async (currentMode) => {
    setPromptsLoading(true);
    try {
      const data = await apiCall(API_ENDPOINTS.AI_PROMPTS_LIST(currentMode));
      if (data.success) {
        setPrompts(data.data);
      }
    } catch (err) {
      console.error('Failed to load prompts:', err);
      setError('Failed to load prompts from server');
    } finally {
      setPromptsLoading(false);
    }
  }, []);

  // Load prompts on mount and when mode changes
  useEffect(() => {
    fetchPrompts(mode);
  }, [mode, fetchPrompts]);

  // One-time migration from localStorage to DB
  useEffect(() => {
    const migrateLocalStoragePrompts = async () => {
      const migrationKey = 'ai_prompts_migrated_to_db';
      if (localStorage.getItem(migrationKey)) return;

      for (const m of ['Furniture', 'Rugs']) {
        const key = m === 'Rugs' ? 'ai_image_generator_prompts_rugs' : 'ai_image_generator_prompts_furniture';
        try {
          const stored = localStorage.getItem(key);
          if (!stored) continue;
          const localPrompts = JSON.parse(stored);
          const defaultIds = new Set(['F001', 'F002', 'R001', 'R002', 'R003']);
          const userPrompts = localPrompts.filter(p => !defaultIds.has(p.id));
          for (const p of userPrompts) {
            try {
              await apiCall(API_ENDPOINTS.AI_PROMPTS_CREATE, {
                method: 'POST',
                body: JSON.stringify({ name: p.name, text: p.text, mode: m, isPublic: false })
              });
            } catch (e) { console.warn('Failed to migrate prompt:', p.name, e); }
          }
        } catch (e) { console.warn('Migration parse error for', m, e); }
      }

      localStorage.setItem(migrationKey, 'true');
      localStorage.removeItem('ai_image_generator_prompts_furniture');
      localStorage.removeItem('ai_image_generator_prompts_rugs');
      fetchPrompts(mode);
    };

    migrateLocalStoragePrompts();
  }, []); // eslint-disable-line

  // Fetch history
  const fetchHistory = useCallback(async (page = 1) => {
    setHistoryLoading(true);
    try {
      const data = await apiCall(API_ENDPOINTS.AI_IMAGE_HISTORY(page, 12));
      if (data.success) {
        setHistoryBatches(data.batches);
        setHistoryTotalPages(data.totalPages);
        setHistoryPage(page);
      }
    } catch (err) {
      console.error('Failed to load history:', err);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  // Load history when switching to history tab
  useEffect(() => {
    if (mainTab === 'history') {
      fetchHistory(1);
      fetchBatchJobs();
    }
  }, [mainTab, fetchHistory]); // eslint-disable-line

  // Auto-refresh batch jobs every 10s while any job is still in progress.
  // When an active job transitions to completed, also refresh the history list
  // so the new batch card appears without the user clicking refresh.
  const prevActiveJobIds = useRef(new Set());
  useEffect(() => {
    if (mainTab !== 'history') return;
    const activeIds = new Set(
      (Array.isArray(batchJobs) ? batchJobs : [])
        .filter(j => {
          const s = String(j?.status || '').toLowerCase();
          return s === 'submitted' || s === 'processing' || s === 'downloading_results';
        })
        .map(j => j.id)
    );
    // Detect any job that was active last render but is no longer active
    let transitioned = false;
    prevActiveJobIds.current.forEach(id => {
      if (!activeIds.has(id)) transitioned = true;
    });
    prevActiveJobIds.current = activeIds;
    if (transitioned) fetchHistory(1);
    if (activeIds.size === 0) return;
    const iv = setInterval(() => { fetchBatchJobs(); }, 10000);
    return () => clearInterval(iv);
  }, [mainTab, batchJobs]); // eslint-disable-line

  // Fetch batch images when expanding
  const fetchBatchImages = useCallback(async (batchId) => {
    if (expandedBatch === batchId) {
      setExpandedBatch(null);
      setBatchImages([]);
      return;
    }
    setExpandedBatch(batchId);
    setBatchImagesLoading(true);
    try {
      const data = await apiCall(API_ENDPOINTS.AI_IMAGE_BATCH(batchId));
      if (data.success) {
        setBatchImages(data.images);
      }
    } catch (err) {
      console.error('Failed to load batch images:', err);
    } finally {
      setBatchImagesLoading(false);
    }
  }, [expandedBatch]);

  const fetchBatchInputs = useCallback(async (batchId) => {
    if (batchInputImages[batchId]) return batchInputImages[batchId];
    try {
      const data = await apiCall(API_ENDPOINTS.AI_IMAGE_BATCH_INPUTS(batchId));
      if (data.success) {
        setBatchInputImages(prev => ({ ...prev, [batchId]: data.inputs }));
        return data.inputs;
      }
    } catch (err) { console.error('Failed to load batch inputs:', err); }
    return [];
  }, [batchInputImages]);

  const handleDeleteInput = async (batchId, inputId) => {
    try {
      await apiCall(API_ENDPOINTS.AI_IMAGE_DELETE_BATCH_INPUT(batchId, inputId), { method: 'DELETE' });
      setBatchInputImages(prev => ({
        ...prev,
        [batchId]: (prev[batchId] || []).filter(i => i.id !== inputId)
      }));
      setSnackbar({ open: true, message: 'Input image deleted' });
    } catch (err) { setSnackbar({ open: true, message: 'Failed to delete' }); }
  };

  const handleDeleteAllInputs = async (batchId) => {
    try {
      await apiCall(API_ENDPOINTS.AI_IMAGE_DELETE_ALL_BATCH_INPUTS(batchId), { method: 'DELETE' });
      setBatchInputImages(prev => ({ ...prev, [batchId]: [] }));
      setHistoryBatches(prev => prev.map(b => b.batchId === batchId ? { ...b, inputImageCount: 0 } : b));
      setSnackbar({ open: true, message: 'All input images deleted for this batch' });
    } catch (err) { setSnackbar({ open: true, message: 'Failed to delete' }); }
  };

  const openCompareDialog = async (batch) => {
    setCompareDialogBatch(batch);
    setCompareIndex(0);
    await fetchBatchInputs(batch.batchId);
    if (!batchImages.length || expandedBatch !== batch.batchId) {
      setBatchImagesLoading(true);
      try {
        const data = await apiCall(API_ENDPOINTS.AI_IMAGE_BATCH(batch.batchId));
        if (data.success) setBatchImages(data.images);
      } catch {}
      setBatchImagesLoading(false);
    }
  };

  const handleModeChange = (newMode) => {
    if (newMode === mode) return;
    setMode(newMode);
    setSelectedPrompts(new Set());
    setSearchText('');
    setPromptFilter('all');
  };

  const filteredPrompts = prompts.filter(p => {
    if (promptFilter === 'mine' && !p.isOwner) return false;
    if (promptFilter === 'public' && !p.isPublic) return false;
    if (!searchText) return true;
    const s = searchText.toLowerCase();
    return p.name.toLowerCase().includes(s) || p.text.toLowerCase().includes(s);
  });

  const onDropProduct = useCallback((acceptedFiles) => {
    setProductImages(prev => [...prev, ...acceptedFiles.map(f => Object.assign(f, { preview: URL.createObjectURL(f) }))]);
  }, []);
  const onDropFixed = useCallback((acceptedFiles) => {
    setFixedImages(prev => [...prev, ...acceptedFiles.map(f => Object.assign(f, { preview: URL.createObjectURL(f) }))]);
  }, []);

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      productImages.forEach(f => { if (f.preview) URL.revokeObjectURL(f.preview); });
      fixedImages.forEach(f => { if (f.preview) URL.revokeObjectURL(f.preview); });
    };
  }, []); // eslint-disable-line

  const removeProductImage = useCallback((idx) => {
    setProductImages(prev => {
      const removed = prev[idx];
      if (removed?.preview) URL.revokeObjectURL(removed.preview);
      return prev.filter((_, i) => i !== idx);
    });
  }, []);

  const removeFixedImage = useCallback((idx) => {
    setFixedImages(prev => {
      const removed = prev[idx];
      if (removed?.preview) URL.revokeObjectURL(removed.preview);
      return prev.filter((_, i) => i !== idx);
    });
  }, []);

  const clearProductImages = useCallback(() => {
    productImages.forEach(f => { if (f.preview) URL.revokeObjectURL(f.preview); });
    setProductImages([]);
  }, [productImages]);

  const clearFixedImages = useCallback(() => {
    fixedImages.forEach(f => { if (f.preview) URL.revokeObjectURL(f.preview); });
    setFixedImages([]);
  }, [fixedImages]);

  const productDropzone = useDropzone({ onDrop: onDropProduct, accept: { 'image/*': ['.png', '.jpg', '.jpeg', '.webp', '.gif'] } });
  const fixedDropzone = useDropzone({ onDrop: onDropFixed, accept: { 'image/*': ['.png', '.jpg', '.jpeg', '.webp', '.gif'] } });

  // Drag-to-reorder handlers
  const handleDragStart = (idx) => { dragIndexRef.current = idx; };
  const handleDragOver = (e, idx) => { e.preventDefault(); setDragOverIndex(idx); };
  const handleDragEnd = () => {
    if (dragIndexRef.current !== null && dragOverIndex !== null && dragIndexRef.current !== dragOverIndex) {
      setProductImages(prev => {
        const items = [...prev];
        const [dragged] = items.splice(dragIndexRef.current, 1);
        items.splice(dragOverIndex, 0, dragged);
        return items;
      });
    }
    dragIndexRef.current = null;
    setDragOverIndex(null);
  };

  const togglePrompt = (id) => {
    setSelectedPrompts(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const selectAll = () => {
    if (selectedPrompts.size === filteredPrompts.length) {
      setSelectedPrompts(new Set());
    } else {
      setSelectedPrompts(new Set(filteredPrompts.map(p => p.id)));
    }
  };

  const openAddPrompt = () => {
    setEditingPrompt(null);
    setPromptForm({ name: '', text: '', isPublic: false });
    setPromptDialogOpen(true);
  };
  const openEditPrompt = (prompt) => {
    setEditingPrompt(prompt);
    setPromptForm({ name: prompt.name, text: prompt.text, isPublic: prompt.isPublic || false });
    setPromptDialogOpen(true);
  };

  const handleSavePrompt = async () => {
    if (!promptForm.name.trim() || !promptForm.text.trim()) {
      setError('Prompt name and text are required');
      return;
    }
    try {
      if (editingPrompt) {
        await apiCall(API_ENDPOINTS.AI_PROMPTS_UPDATE(editingPrompt.id), {
          method: 'PUT',
          body: JSON.stringify({ name: promptForm.name.trim(), text: promptForm.text.trim(), mode, isPublic: promptForm.isPublic })
        });
        setSnackbar({ open: true, message: 'Prompt updated!' });
      } else {
        await apiCall(API_ENDPOINTS.AI_PROMPTS_CREATE, {
          method: 'POST',
          body: JSON.stringify({ name: promptForm.name.trim(), text: promptForm.text.trim(), mode, isPublic: promptForm.isPublic })
        });
        setSnackbar({ open: true, message: 'Prompt added!' });
      }
      setPromptDialogOpen(false);
      fetchPrompts(mode);
    } catch (err) {
      setError(`Failed to save prompt: ${err.message}`);
    }
  };

  const handleDeletePrompt = async (id) => {
    try {
      await apiCall(API_ENDPOINTS.AI_PROMPTS_DELETE(id), { method: 'DELETE' });
      setSelectedPrompts(prev => { const next = new Set(prev); next.delete(id); return next; });
      setSnackbar({ open: true, message: 'Prompt deleted.' });
      fetchPrompts(mode);
    } catch (err) {
      setError(`Failed to delete prompt: ${err.message}`);
    }
  };

  const numPairs = Math.floor(productImages.length / 2);
  const numSilos = productImages.length;
  const numRequests = imageMode === 'pairs' ? numPairs : numSilos;
  const selectedPromptsList = prompts.filter(p => selectedPrompts.has(p.id));
  const totalGenerations = numRequests * selectedPromptsList.length * repeatCount;

  // Generate a single image request (shared between generate and retry)
  const generateSingleImage = async (prompt, img1, img2, batchId, token, signal) => {
    const formData = new FormData();
    formData.append('PromptName', prompt.name);
    formData.append('PromptText', prompt.text);
    formData.append('Mode', mode);
    formData.append('AspectRatio', aspectRatio);
    formData.append('Resolution', resolution);
    formData.append('Model', imageModel);
    formData.append('AdditionalInstructions', additionalInstructions);
    formData.append('BatchId', batchId);
    formData.append('ProductImage1', img1);
    if (img2) formData.append('ProductImage2', img2);
    // Bake the toggle choice into the saved filename so history downloads
    // stay consistent — the simple-name preference at *generation time*
    // sticks to that image forever, even if the user flips the toggle later.
    {
      const pair = img2 ? `${img1.name} + ${img2.name}` : img1.name;
      const chosenName = getDownloadFilename({ pair });
      formData.append('FileName', chosenName);
    }
    fixedImages.forEach(fix => formData.append('FixedImages', fix));
    const response = await fetch(`${API_BASE_URL}/api/AIImageGenerator/generate`, {
      method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: formData, signal,
    });
    const responseText = await response.text();
    if (!response.ok) console.error(`[AI Generator] ${response.status} Error:`, responseText);
    let data = {};
    try { data = JSON.parse(responseText); } catch (e) { data = { error: responseText }; }
    if (data.saveError) console.warn(`[AI Generator] S3 save error: ${data.saveError}`);
    return data;
  };

  // Fetch batch jobs for the batch jobs panel in History tab
  var fetchBatchJobs = async function() {
    try {
      setBatchJobsLoading(true);
      var token = localStorage.getItem('jwtToken');
      var res = await fetch(API_BASE_URL + '/api/AIImageGenerator/batch-jobs', {
        headers: { 'Authorization': 'Bearer ' + token }
      });
      if (res.ok) {
        var text = await res.text();
        try {
          var data = JSON.parse(text);
          if (data.success && Array.isArray(data.jobs)) {
            setBatchJobs(data.jobs);
          }
        } catch (pe) { /* not JSON */ }
      }
    } catch (e) { console.error('Failed to load batch jobs:', e); }
    setBatchJobsLoading(false);
  };

  // Batch generate via Gemini Batch API (50% cost reduction, async processing)
  var handleBatchGenerate = async function() {
    if (selectedPromptsList.length === 0) { setError('Please select at least one prompt'); return; }
    if (imageMode === 'pairs' && productImages.length < 2) { setError('Please upload at least 2 product images to form a pair'); return; }
    if (imageMode === 'silo' && productImages.length < 1) { setError('Please upload at least 1 product image'); return; }

    setError(null);
    setBatchSubmitting(true);
    setBatchLog(['Preparing batch request with ' + selectedPromptsList.length + ' prompt(s) and ' + productImages.length + ' image(s)...']);

    try {
      var token = localStorage.getItem('jwtToken');
      var formData = new FormData();
      var batchId = Date.now().toString();

      selectedPromptsList.forEach(function(p) {
        formData.append('Prompts', p.text);
        formData.append('PromptNames', p.name);
      });

      formData.append('Mode', mode);
      formData.append('AspectRatio', aspectRatio);
      formData.append('Resolution', resolution);
      formData.append('Model', imageModel);
      formData.append('AdditionalInstructions', additionalInstructions);
      formData.append('BatchId', batchId);
      formData.append('ImageMode', imageMode);
      // Send ALL product images; the backend decides how to group them based on ImageMode
      // (silo = one unit per image, pairs = one unit per consecutive pair).
      productImages.forEach(function(img) { formData.append('ProductImages', img); });
      fixedImages.forEach(function(f) { formData.append('FixedImages', f); });

      setBatchLog(function(prev) { return prev.concat(['Uploading images and submitting to Gemini Batch API...']); });

      var response = await fetch(API_BASE_URL + '/api/AIImageGenerator/batch-generate', {
        method: 'POST',
        headers: { 'Authorization': 'Bearer ' + token },
        body: formData,
      });

      var text = await response.text();
      var result = {};
      try { result = JSON.parse(text); } catch (e) { result = { error: text }; }

      if (result.success) {
        var jobId = result.batchJobId || '';
        var batchName = result.geminiBatchName || '';
        setBatchLog(function(prev) { return prev.concat([
          'Batch submitted successfully!',
          'Job ID: #' + jobId,
          batchName ? 'Gemini Batch: ' + batchName : '',
          'Processing at 50% cost. Polling for results in background...',
          'You will be notified when complete. Check History tab for status.'
        ].filter(Boolean)); });
        setSnackbar({ open: true, message: 'Batch submitted (Job #' + jobId + '). Check Batch Jobs in History tab.' });
        fetchBatchJobs();
      } else {
        var errMsg = result.error || result.message || 'Unknown error';
        setBatchLog(function(prev) { return prev.concat(['ERROR: ' + errMsg]); });
        setError('Batch failed: ' + errMsg);
      }
    } catch (err) {
      setBatchLog(function(prev) { return prev.concat(['ERROR: ' + err.message]); });
      setError('Batch error: ' + err.message);
    }
    setBatchSubmitting(false);
  };

  // Generate images
  const handleGenerate = async () => {
    if (selectedPromptsList.length === 0) { setError('Please select at least one prompt'); return; }
    if (imageMode === 'pairs' && productImages.length < 2) { setError('Please upload at least 2 product images to form a pair'); return; }
    if (imageMode === 'silo' && productImages.length < 1) { setError('Please upload at least 1 product image'); return; }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    setGenerating(true);
    setError(null);
    setGeneratedImages([]);
    setProgress({ current: 0, total: totalGenerations });

    const batchId = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    setCurrentBatchId(batchId);

    const token = localStorage.getItem('jwtToken');
    const results = [];
    let current = 0;
    let aborted = false;

    if (imageMode === 'pairs') {
      for (let i = 0; i < numPairs && !aborted; i++) {
        const img1 = productImages[i * 2];
        const img2 = productImages[i * 2 + 1];
        for (const prompt of selectedPromptsList) {
          if (aborted) break;
          for (let r = 0; r < repeatCount; r++) {
            if (aborted) break;
          try {
            const data = await generateSingleImage(prompt, img1, img2, batchId, token, controller.signal);
            if (data.success && data.image) {
              const runLabel = repeatCount > 1 ? ` (run ${r + 1})` : '';
              results.push({ id: `gen_${Date.now()}_${i}_${prompt.id}_${r}`, image: `data:${data.mimeType};base64,${data.image}`, promptName: `${prompt.name}${runLabel}`, pair: `${img1.name} + ${img2.name}`, text: data.text, s3Url: data.s3Url, fileName: data.fileName, sourceImages: [img1.preview, img2.preview], retryContext: { prompt, img1, img2 } });
            } else {
              const errMsg = data.details ? `${data.error} - ${typeof data.details === 'string' ? data.details.substring(0, 200) : JSON.stringify(data.details).substring(0, 200)}` : (data.error || 'Generation failed');
              results.push({ id: `err_${Date.now()}_${i}_${prompt.id}_${r}`, error: errMsg, promptName: prompt.name, pair: `${img1.name} + ${img2.name}`, sourceImages: [img1.preview, img2.preview], retryContext: { prompt, img1, img2 } });
            }
          } catch (err) {
            if (err.name === 'AbortError') { aborted = true; break; }
            console.error('[AI Generator] Exception:', err);
            results.push({ id: `err_${Date.now()}_${i}_${prompt.id}_${r}`, error: err.message, promptName: prompt.name, pair: `${img1.name} + ${img2.name}`, sourceImages: [img1.preview, img2.preview], retryContext: { prompt, img1, img2 } });
          }
          current++;
          setProgress({ current, total: totalGenerations });
          setGeneratedImages([...results]);
          }
        }
      }
    } else {
      for (let i = 0; i < numSilos && !aborted; i++) {
        const img = productImages[i];
        for (const prompt of selectedPromptsList) {
          if (aborted) break;
          for (let r = 0; r < repeatCount; r++) {
            if (aborted) break;
          try {
            const data = await generateSingleImage(prompt, img, null, batchId, token, controller.signal);
            if (data.success && data.image) {
              const runLabel = repeatCount > 1 ? ` (run ${r + 1})` : '';
              results.push({ id: `gen_${Date.now()}_${i}_${prompt.id}_${r}`, image: `data:${data.mimeType};base64,${data.image}`, promptName: `${prompt.name}${runLabel}`, pair: img.name, text: data.text, s3Url: data.s3Url, fileName: data.fileName, sourceImages: [img.preview], retryContext: { prompt, img1: img, img2: null } });
            } else {
              const errMsg = data.details ? `${data.error} - ${typeof data.details === 'string' ? data.details.substring(0, 200) : JSON.stringify(data.details).substring(0, 200)}` : (data.error || 'Generation failed');
              results.push({ id: `err_${Date.now()}_${i}_${prompt.id}_${r}`, error: errMsg, promptName: prompt.name, pair: img.name, sourceImages: [img.preview], retryContext: { prompt, img1: img, img2: null } });
            }
          } catch (err) {
            if (err.name === 'AbortError') { aborted = true; break; }
            console.error('[AI Generator] Exception:', err);
            results.push({ id: `err_${Date.now()}_${i}_${prompt.id}_${r}`, error: err.message, promptName: prompt.name, pair: img.name, sourceImages: [img.preview], retryContext: { prompt, img1: img, img2: null } });
          }
          current++;
          setProgress({ current, total: totalGenerations });
          setGeneratedImages([...results]);
          }
        }
      }
    }

    abortControllerRef.current = null;
    setGenerating(false);
    const successCount = results.filter(r => !r.error).length;
    if (aborted) {
      setSnackbar({ open: true, message: `Generation aborted. ${successCount} image${successCount !== 1 ? 's' : ''} generated before cancellation.` });
    } else {
      setSnackbar({ open: true, message: `Generation complete! ${successCount}/${totalGenerations} images generated.` });
      if (backgroundMode && mainTab !== 'generator') setMainTab('generator');
    }

  };

  const handleAbortGeneration = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  };

  // Retry failed generations
  const handleRetryFailed = async () => {
    const failedResults = generatedImages.filter(r => r.error && r.retryContext);
    if (failedResults.length === 0) return;

    const controller = new AbortController();
    abortControllerRef.current = controller;
    setGenerating(true);
    setError(null);
    setProgress({ current: 0, total: failedResults.length });

    const batchId = currentBatchId || (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);
    const token = localStorage.getItem('jwtToken');
    let current = 0;
    let aborted = false;

    // Build new results: keep successes, replace failures with retries
    const newResults = generatedImages.filter(r => !r.error);

    for (const failedResult of failedResults) {
      if (aborted) break;
      const { prompt, img1, img2 } = failedResult.retryContext;
      try {
        const data = await generateSingleImage(prompt, img1, img2, batchId, token, controller.signal);
        if (data.success && data.image) {
          newResults.push({ id: `gen_${Date.now()}_retry_${current}`, image: `data:${data.mimeType};base64,${data.image}`, promptName: failedResult.promptName, pair: failedResult.pair, text: data.text, s3Url: data.s3Url, sourceImages: failedResult.sourceImages, retryContext: failedResult.retryContext });
        } else {
          const errMsg = data.details ? `${data.error} - ${typeof data.details === 'string' ? data.details.substring(0, 200) : JSON.stringify(data.details).substring(0, 200)}` : (data.error || 'Generation failed');
          newResults.push({ ...failedResult, id: `err_${Date.now()}_retry_${current}`, error: errMsg });
        }
      } catch (err) {
        if (err.name === 'AbortError') { aborted = true; break; }
        newResults.push({ ...failedResult, id: `err_${Date.now()}_retry_${current}`, error: err.message });
      }
      current++;
      setProgress({ current, total: failedResults.length });
      setGeneratedImages([...newResults]);
    }

    abortControllerRef.current = null;
    setGenerating(false);
    const retrySuccesses = newResults.filter(r => !r.error).length - generatedImages.filter(r => !r.error).length;
    setSnackbar({ open: true, message: aborted ? 'Retry aborted.' : `Retry complete! ${retrySuccesses}/${failedResults.length} recovered.` });

  };

  // Regenerate selected successful images (same prompt + source, new result)
  const handleRegenerateSelected = async (overrideIds) => {
    const ids = (overrideIds instanceof Set) ? overrideIds : regenSelected;
    const toRegen = generatedImages.filter(r => !r.error && r.retryContext && ids.has(r.id));
    if (toRegen.length === 0) return;

    const controller = new AbortController();
    abortControllerRef.current = controller;
    setGenerating(true);
    setError(null);
    setProgress({ current: 0, total: toRegen.length });

    const batchId = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const token = localStorage.getItem('jwtToken');
    let current = 0;
    let aborted = false;
    const newResults = [];

    for (const orig of toRegen) {
      if (aborted) break;
      const { prompt, img1, img2 } = orig.retryContext;
      try {
        const data = await generateSingleImage(prompt, img1, img2, batchId, token, controller.signal);
        if (data.success && data.image) {
          newResults.push({ id: `regen_${Date.now()}_${current}`, image: `data:${data.mimeType};base64,${data.image}`, promptName: orig.promptName, pair: orig.pair, text: data.text, s3Url: data.s3Url, sourceImages: orig.sourceImages, retryContext: orig.retryContext });
        } else {
          newResults.push({ id: `regen_err_${Date.now()}_${current}`, error: data.error || 'Failed', promptName: orig.promptName, pair: orig.pair, sourceImages: orig.sourceImages, retryContext: orig.retryContext });
        }
      } catch (err) {
        if (err.name === 'AbortError') { aborted = true; break; }
        newResults.push({ id: `regen_err_${Date.now()}_${current}`, error: err.message, promptName: orig.promptName, pair: orig.pair, sourceImages: orig.sourceImages, retryContext: orig.retryContext });
      }
      current++;
      setProgress({ current, total: toRegen.length });
    }

    // Append new results to existing (keep originals, add new variations)
    setGeneratedImages(prev => [...prev, ...newResults]);

    abortControllerRef.current = null;
    setGenerating(false);
    setRegenSelectMode(false);
    setRegenSelected(new Set());
    const successes = newResults.filter(r => !r.error).length;
    setSnackbar({ open: true, message: aborted ? 'Regeneration aborted.' : `Regenerated ${successes}/${toRegen.length} images. New variations added below.` });

  };

  const toggleRegenSelect = (id) => {
    setRegenSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const CROP_PRESETS = [
    { name: 'Amazon', size: '2000x2000' },
    { name: 'Wayfair', size: '3000x3000' },
    { name: 'Website', size: '1200x1200' },
    { name: 'Social', size: '1080x1080' },
    { name: 'Wide Banner', size: '1920x1080' },
    { name: 'Portrait', size: '1080x1350' },
  ];

  const downloadWithCrop = (imageData, filename, targetWidth, targetHeight) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = targetWidth;
      canvas.height = targetHeight;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, targetWidth, targetHeight);
      const scale = Math.min(targetWidth / img.width, targetHeight / img.height);
      const w = img.width * scale;
      const h = img.height * scale;
      ctx.drawImage(img, (targetWidth - w) / 2, (targetHeight - h) / 2, w, h);
      const link = document.createElement('a');
      link.href = canvas.toDataURL('image/jpeg', 0.95);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    };
    img.src = imageData;
  };

  const openResizeDialog = (imageData, filename) => {
    setResizeImage(imageData);
    setResizeFilename(filename);
    setCustomWidth('');
    setCustomHeight('');
    setKeepAspectRatio(true);
    // Get original dimensions
    const img = new Image();
    img.onload = () => { setResizeOriginalDims({ w: img.width, h: img.height }); };
    img.src = imageData;
    setResizeDialogOpen(true);
  };

  const handleCustomWidthChange = (val) => {
    setCustomWidth(val);
    if (keepAspectRatio && resizeOriginalDims.w > 0 && val) {
      const ratio = resizeOriginalDims.h / resizeOriginalDims.w;
      setCustomHeight(Math.round(Number(val) * ratio).toString());
    }
  };

  const handleCustomHeightChange = (val) => {
    setCustomHeight(val);
    if (keepAspectRatio && resizeOriginalDims.h > 0 && val) {
      const ratio = resizeOriginalDims.w / resizeOriginalDims.h;
      setCustomWidth(Math.round(Number(val) * ratio).toString());
    }
  };

  const handleSuggestPrompts = () => {
    if (productImages.length === 0 || prompts.length === 0) return;
    // Client-side suggestions based on mode — no API call
    var modePrompts = prompts.filter(function(p) { return p.mode === mode || p.mode === 'Both'; });
    var results = modePrompts.slice(0, 6).map(function(p) { return { id: p.id, name: p.name }; });
    setSuggestedPrompts(results);
  };

  const handleGenerateAiPrompts = async () => {
    if (productImages.length === 0) { setError('Upload images first'); return; }
    setGeneratingAiPrompts(true);
    setAiGeneratedPrompts([]);
    setError(null);
    try {
      const token = localStorage.getItem('jwtToken');
      const formData = new FormData();
      const imagesToSend = productImages.slice(0, 7);
      imagesToSend.forEach(img => formData.append('images', img));
      formData.append('mode', mode);
      const response = await fetch(API_ENDPOINTS.AI_IMAGE_GENERATE_PROMPTS, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });
      const data = await response.json();
      if (data.success && data.prompts && data.prompts.length > 0) {
        setAiGeneratedPrompts(data.prompts);
        setAiPromptsDialogOpen(true);
      } else {
        setError(data.message || data.error || 'Failed to generate prompts');
      }
    } catch (err) {
      setError('Failed to generate AI prompts: ' + err.message);
    } finally {
      setGeneratingAiPrompts(false);
    }
  };

  const openPreview = (images, startIndex) => {
    setPreviewImages(images);
    setPreviewIndex(startIndex);
    setPreviewOpen(true);
    setZoomActive(false);
  };

  // Touch-swipe tracking for the lightbox. Horizontal delta > 50px and > vertical delta
  // counts as a swipe; anything smaller is treated as a tap / zoom gesture.
  const previewTouchRef = useRef(null);
  const handlePreviewTouchStart = (e) => {
    const t = e.changedTouches[0];
    previewTouchRef.current = { x: t.clientX, y: t.clientY };
  };
  const handlePreviewTouchEnd = (e) => {
    const start = previewTouchRef.current;
    previewTouchRef.current = null;
    if (!start || previewImages.length <= 1 || zoomActive) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    if (Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(dy)) return; // not a horizontal swipe
    if (dx < 0) {
      // swipe left = next
      setPreviewIndex(prev => (prev + 1) % previewImages.length);
    } else {
      // swipe right = previous
      setPreviewIndex(prev => (prev - 1 + previewImages.length) % previewImages.length);
    }
    setZoomActive(false);
  };

  // Keyboard navigation for history compare dialog
  useEffect(() => {
    if (!compareDialogBatch || !batchImages.length) return;
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        setCompareIndex(prev => Math.max(0, prev - 1));
      } else if (e.key === 'ArrowRight') {
        setCompareIndex(prev => Math.min(batchImages.length - 1, prev + 1));
      } else if (e.key === 'Escape') {
        setCompareDialogBatch(null);
        setCompareIndex(0);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [compareDialogBatch, batchImages.length]);

  // Keyboard navigation for lightbox
  useEffect(() => {
    if (!previewOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        setPreviewIndex(prev => (prev - 1 + previewImages.length) % previewImages.length);
        setZoomActive(false);
      } else if (e.key === 'ArrowRight') {
        setPreviewIndex(prev => (prev + 1) % previewImages.length);
        setZoomActive(false);
      } else if (e.key === 'Escape') {
        setPreviewOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [previewOpen, previewImages.length]);

  const downloadImage = (imageData, filename) => {
    const link = document.createElement('a');
    link.href = imageData;
    link.download = filename || 'generated-image.png';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadImageAsJpg = (imageData, filename) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      const jpgData = canvas.toDataURL('image/jpeg', 0.95);
      const link = document.createElement('a');
      link.href = jpgData;
      link.download = (filename || 'generated-image').replace(/\.png$/i, '') + '.jpg';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    };
    img.src = imageData;
  };

  const downloadBatchZip = async (batchId) => {
    try {
      const token = localStorage.getItem('jwtToken');
      const response = await fetch(API_ENDPOINTS.AI_IMAGE_BATCH_DOWNLOAD(batchId), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `ai_images_${batchId.substring(0, 8)}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(`Failed to download: ${err.message}`);
    }
  };

  const getDownloadFilename = (result) => {
    // If the backend has already returned the canonical filename for this
    // generation (it bakes in the toggle choice from generation time),
    // honor that — the user's selection sticks to the image regardless of
    // how the toggle is set when they download. Strip extension; download
    // helpers re-add the right one based on type (PNG/JPG).
    if (result?.fileName) {
      return result.fileName.replace(/\.[^.]+$/, '');
    }
    // result.pair is either "img1.name + img2.name" (pairs mode) or just
    // "img.name" (single mode). Strip the file extension off each part.
    const names = (result?.pair || '').split(' + ').map(n => n.replace(/\.[^.]+$/, ''));
    if (simpleNaming) {
      // Just the product image name. For pairs, prefer the one whose
      // filename does NOT contain "detail" (Safavieh's convention is that
      // the *_detail.jpg variant is the close-up, not the hero shot, so
      // the non-detail name reads cleaner). If both contain "detail" or
      // neither does, fall back to the first.
      const nonDetail = names.find(n => !/detail/i.test(n));
      return nonDetail || names[0] || 'image';
    }
    const now = new Date();
    const dt = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}_${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}${String(now.getSeconds()).padStart(2, '0')}`;
    return `${names.join('+')}_${dt}`;
  };

  const downloadSingleFromHistory = async (imageId, fileName) => {
    try {
      const token = localStorage.getItem('jwtToken');
      const response = await fetch(API_ENDPOINTS.AI_IMAGE_DOWNLOAD_SINGLE(imageId), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName || 'image.png';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(`Failed to download: ${err.message}`);
    }
  };

  // Build proxied image URL for history images (S3 objects are private)
  const getProxiedImageUrl = (imageId) => {
    const token = localStorage.getItem('jwtToken');
    return `${API_ENDPOINTS.AI_IMAGE_PROXY(imageId)}${token ? `?token=${token}` : ''}`;
  };

  // Helper to get displayable image src for a collection image
  const getCollectionImageSrc = (colImg) => {
    if (colImg.imageSrc) return colImg.imageSrc;
    if (colImg.s3Url) {
      // Try to extract imageId from s3Url and use proxy
      // s3Url typically contains the path; we need the imageId from the history
      // For saved images, we store imageSrc at save time, so this is a fallback
      return colImg.s3Url;
    }
    return '';
  };

  // Build interleaved preview for comparison mode: source(s) -> generated -> source(s) -> generated ...
  const comparisonPreview = [];
  const comparisonIndexMap = {};
  const comparisonSuccessResults = generatedImages.filter(r => !r.error);
  comparisonSuccessResults.forEach((r) => {
    if (r.sourceImages) {
      r.sourceImages.forEach((src, si) => {
        comparisonIndexMap[`${r.id}_src_${si}`] = comparisonPreview.length;
        comparisonPreview.push({ src, label: `Source ${si + 1} — ${r.pair}` });
      });
    }
    comparisonIndexMap[`${r.id}_gen`] = comparisonPreview.length;
    comparisonPreview.push({ src: r.image, label: `Generated — ${r.promptName}` });
  });

  const allSelected = filteredPrompts.length > 0 && selectedPrompts.size === filteredPrompts.length;

  // Get current selected collection object
  const currentCollection = collections.find(c => c.id === selectedCollection) || null;

  // Get images selected for comparison in collections
  const comparisonImages = currentCollection
    ? currentCollection.images.filter((_, idx) => collectionCompareSelected.has(idx))
    : [];

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto', position: 'relative' }}>
      <InterfaceHeader title="AI Image Generator" color={AI_COLOR} category="ai" />

      {/* Main Tab Toggle: Generator / History / Collections */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
        <Tabs value={mainTab} onChange={(e, v) => setMainTab(v)}
          variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile
          sx={{ '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '0.95rem' }, '& .MuiTabs-indicator': { bgcolor: AI_COLOR } }}>
          <Tab icon={<FaMagic size={14} />} iconPosition="start" label="Generator" value="generator" />
          <Tab icon={<FaHistory size={14} />} iconPosition="start" label="History" value="history" />
          <Tab icon={<MdFolderSpecial size={16} />} iconPosition="start" label="Collections" value="collections" />
        </Tabs>
      </Box>

      {/* ==================== GENERATOR TAB ==================== */}
      {mainTab === 'generator' && (
        <>
          {/* Mode Toggle */}
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 3 }}>
            <Button variant={mode === 'Furniture' ? 'contained' : 'outlined'} startIcon={<FaChair />}
              onClick={() => handleModeChange('Furniture')}
              sx={{ bgcolor: mode === 'Furniture' ? AI_COLOR : 'transparent', borderColor: AI_COLOR, color: mode === 'Furniture' ? 'white' : theme.palette.text.primary, '&:hover': { bgcolor: mode === 'Furniture' ? TOOL_COLORS.AI.hover : `${AI_COLOR}10` } }}>
              Furniture
            </Button>
            <Button variant={mode === 'Rugs' ? 'contained' : 'outlined'} startIcon={<FaBorderAll />}
              onClick={() => handleModeChange('Rugs')}
              sx={{ bgcolor: mode === 'Rugs' ? AI_COLOR : 'transparent', borderColor: AI_COLOR, color: mode === 'Rugs' ? 'white' : theme.palette.text.primary, '&:hover': { bgcolor: mode === 'Rugs' ? TOOL_COLORS.AI.hover : `${AI_COLOR}10` } }}>
              Rugs
            </Button>
          </Box>

          {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}

          {generating && (
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="body2">Generating images...</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2">{progress.current}/{progress.total}</Typography>
                  <Button size="small" variant="outlined" color="error" startIcon={<FaStop size={10} />}
                    onClick={handleAbortGeneration}
                    sx={{ fontSize: '0.75rem', py: 0.25, textTransform: 'none' }}>
                    Abort
                  </Button>
                </Box>
              </Box>
              <LinearProgress variant="determinate" value={(progress.current / progress.total) * 100}
                sx={{ height: 8, borderRadius: 4, bgcolor: `${AI_COLOR}20`, '& .MuiLinearProgress-bar': { bgcolor: AI_COLOR } }} />
            </Box>
          )}

          <Grid container spacing={3}>
            {/* Left Panel - Prompts */}
            <Grid item xs={12} md={5}>
              <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <MdAutoAwesome color={AI_COLOR} /> {mode} Prompts ({filteredPrompts.length}/{prompts.length})
                </Typography>

                <TextField size="small" placeholder="Search prompts..." value={searchText} onChange={(e) => setSearchText(e.target.value)}
                  InputProps={{
                    startAdornment: <FaSearch size={14} style={{ marginRight: 8, opacity: 0.5 }} />,
                    endAdornment: searchText && <IconButton size="small" onClick={() => setSearchText('')}><FaTimes size={12} /></IconButton>,
                  }}
                  sx={{ mb: 1 }} fullWidth />

                <Tabs value={promptFilter} onChange={(e, v) => setPromptFilter(v)}
                  variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile
                  sx={{ mb: 1, minHeight: 36, '& .MuiTab-root': { minHeight: 36, py: 0.5, fontSize: '0.8rem', textTransform: 'none' }, '& .MuiTabs-indicator': { bgcolor: AI_COLOR } }}>
                  <Tab label="All" value="all" />
                  <Tab label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><FaLock size={10} /> My Prompts</Box>} value="mine" />
                  <Tab label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><FaGlobe size={10} /> Shared</Box>} value="public" />
                </Tabs>

                <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Button size="small" onClick={selectAll} variant="outlined"
                    sx={{ borderColor: AI_COLOR, color: theme.palette.text.primary }}>
                    {allSelected ? 'Deselect All' : 'Select All'}
                  </Button>
                  <Button size="small" onClick={openAddPrompt} startIcon={<FaPlus />} variant="contained"
                    sx={{ bgcolor: AI_COLOR, '&:hover': { bgcolor: TOOL_COLORS.AI.hover } }}>
                    Add Prompt
                  </Button>
                  <Box sx={{ flex: 1 }} />
                  <Chip label={`${selectedPrompts.size} selected`} size="small"
                    sx={{ bgcolor: `${AI_COLOR}20`, color: theme.palette.text.primary }} />
                </Box>

                <Box sx={{ flex: 1, overflow: 'auto' }}>
                  {promptsLoading ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <CircularProgress size={24} sx={{ color: AI_COLOR }} />
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Loading prompts...</Typography>
                    </Box>
                  ) : filteredPrompts.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography color="text.secondary">No prompts found. Click "Add Prompt" to create one.</Typography>
                    </Box>
                  ) : (
                    filteredPrompts.map(prompt => (
                      <Card key={prompt.id} sx={{
                        mb: 1, cursor: 'pointer',
                        border: selectedPrompts.has(prompt.id) ? `2px solid ${AI_COLOR}` : `1px solid ${theme.palette.divider}`,
                        bgcolor: selectedPrompts.has(prompt.id) ? (isDark ? `${AI_COLOR}15` : `${AI_COLOR}08`) : 'transparent',
                        transition: 'all 0.15s ease', '&:hover': { borderColor: AI_COLOR },
                      }} onClick={() => togglePrompt(prompt.id)}>
                        <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                            <Checkbox checked={selectedPrompts.has(prompt.id)} size="small"
                              sx={{ p: 0, color: AI_COLOR, '&.Mui-checked': { color: AI_COLOR } }}
                              onClick={(e) => e.stopPropagation()} onChange={() => togglePrompt(prompt.id)} />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>{prompt.name}</Typography>
                                {prompt.isPublic && (
                                  <Chip icon={<FaGlobe size={8} />} label="Shared" size="small"
                                    sx={{ height: 18, fontSize: '0.6rem', bgcolor: isDark ? 'rgba(30,136,229,0.15)' : '#e3f2fd', color: isDark ? '#90caf9' : '#1565c0' }} />
                                )}
                                {!prompt.isOwner && (
                                  <Chip icon={<FaUser size={8} />} label="Others" size="small"
                                    sx={{ height: 18, fontSize: '0.6rem', bgcolor: isDark ? 'rgba(156,39,176,0.15)' : '#f3e5f5', color: isDark ? '#ce93d8' : '#7b1fa2' }} />
                                )}
                              </Box>
                              <Typography variant="caption" color="text.secondary" sx={{
                                display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                              }}>{prompt.text}</Typography>
                            </Box>
                            {prompt.isOwner && (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                <Tooltip title="Edit prompt">
                                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); openEditPrompt(prompt); }}
                                    sx={{ color: theme.palette.text.secondary }}>
                                    <FaEdit size={12} />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete prompt">
                                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDeletePrompt(prompt.id); }}
                                    sx={{ color: 'error.main' }}>
                                    <FaTrash size={10} />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            )}
                          </Box>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </Box>
              </Paper>
            </Grid>

            {/* Right Panel - Images, Settings & Results */}
            <Grid item xs={12} md={7}>
              <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
                {/* Product Images */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FaImage color={AI_COLOR} /> Product Images
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Button size="small" variant={imageMode === 'pairs' ? 'contained' : 'outlined'}
                      onClick={() => setImageMode('pairs')}
                      sx={{ fontSize: '0.75rem', py: 0.5, bgcolor: imageMode === 'pairs' ? AI_COLOR : 'transparent', borderColor: AI_COLOR, color: imageMode === 'pairs' ? 'white' : theme.palette.text.primary, '&:hover': { bgcolor: imageMode === 'pairs' ? TOOL_COLORS.AI.hover : `${AI_COLOR}10` } }}>
                      Detail (Pairs)
                    </Button>
                    <Button size="small" variant={imageMode === 'silo' ? 'contained' : 'outlined'}
                      onClick={() => setImageMode('silo')}
                      sx={{ fontSize: '0.75rem', py: 0.5, bgcolor: imageMode === 'silo' ? AI_COLOR : 'transparent', borderColor: AI_COLOR, color: imageMode === 'silo' ? 'white' : theme.palette.text.primary, '&:hover': { bgcolor: imageMode === 'silo' ? TOOL_COLORS.AI.hover : `${AI_COLOR}10` } }}>
                      Silo (Singles)
                    </Button>
                  </Box>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
                  {imageMode === 'pairs'
                    ? 'Upload product images in pairs. Images 1&2 = Pair 1, Images 3&4 = Pair 2, etc.'
                    : 'Upload individual silo product images. Each image generates a separate request.'}
                </Typography>

                <Box {...productDropzone.getRootProps()} sx={{
                  border: `2px dashed ${productDropzone.isDragActive ? AI_COLOR : theme.palette.divider}`,
                  borderRadius: 2, p: 2, textAlign: 'center', cursor: 'pointer', mb: 1,
                  bgcolor: productDropzone.isDragActive ? `${AI_COLOR}10` : 'transparent',
                  '&:hover': { borderColor: AI_COLOR, bgcolor: `${AI_COLOR}05` },
                }}>
                  <input {...productDropzone.getInputProps()} />
                  <FaImage size={24} color={AI_COLOR} style={{ opacity: 0.5 }} />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {imageMode === 'pairs' ? 'Drop product image pairs here, or click to select' : 'Drop silo product images here, or click to select'}
                  </Typography>
                </Box>

                {productImages.length > 0 && (
                  <Box sx={{ mb: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', fontStyle: 'italic' }}>
                        <FaGripVertical size={8} style={{ verticalAlign: 'middle', marginRight: 2 }} /> Drag to reorder
                      </Typography>
                      <Button size="small" variant="outlined"
                        startIcon={generatingAiPrompts ? <CircularProgress size={12} color="inherit" /> : <MdAutoAwesome size={12} />}
                        onClick={handleGenerateAiPrompts}
                        disabled={generatingAiPrompts || productImages.length === 0}
                        sx={{ fontSize: '0.65rem', textTransform: 'none', borderColor: '#F59E0B', color: '#F59E0B', py: 0.25, px: 1,
                          '&:hover': { bgcolor: 'rgba(245,158,11,0.08)', borderColor: '#D97706' } }}>
                        {generatingAiPrompts ? 'Generating...' : `AI Prompt Generator${productImages.length > 7 ? ' (first 7)' : ''}`}
                      </Button>
                    </Box>
                    <Button size="small" startIcon={<FaTimes size={10} />} onClick={clearProductImages}
                      sx={{ fontSize: '0.7rem', color: 'error.main', textTransform: 'none' }}>
                      Clear All
                    </Button>
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {productImages.map((file, idx) => (
                      <Box key={`${file.name}_${idx}`} draggable
                        onDragStart={() => handleDragStart(idx)}
                        onDragOver={(e) => handleDragOver(e, idx)}
                        onDragEnd={handleDragEnd}
                        sx={{ position: 'relative', width: 64, height: 64, cursor: 'grab',
                          opacity: dragOverIndex === idx ? 0.5 : 1,
                          outline: dragOverIndex === idx ? `2px dashed ${AI_COLOR}` : 'none',
                          borderRadius: 1, transition: 'opacity 0.15s' }}>
                        <img src={file.preview} alt={file.name}
                          onClick={() => openPreview(productImages.map((f) => ({ src: f.preview, label: f.name })), idx)}
                          style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 4, cursor: 'pointer', pointerEvents: 'auto' }}
                          draggable={false} />
                        {imageMode === 'pairs' ? (
                          <Chip label={`${Math.floor(idx / 2) + 1}${idx % 2 === 0 ? 'A' : 'B'}`} size="small"
                            sx={{ position: 'absolute', top: -4, left: -4, height: 18, fontSize: '0.6rem', bgcolor: idx % 2 === 0 ? 'success.main' : 'warning.main', color: 'white' }} />
                        ) : (
                          <Chip label={`${idx + 1}`} size="small"
                            sx={{ position: 'absolute', top: -4, left: -4, height: 18, fontSize: '0.6rem', bgcolor: AI_COLOR, color: 'white' }} />
                        )}
                        <IconButton size="small" sx={{ position: 'absolute', top: -4, right: -4, bgcolor: 'error.main', color: 'white', width: 18, height: 18, '&:hover': { bgcolor: 'error.dark' } }}
                          onClick={() => removeProductImage(idx)}>
                          <FaTimes size={8} />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                  </Box>
                )}
                <Typography variant="caption" color="text.secondary" sx={{ mb: 2 }}>
                  {imageMode === 'pairs' ? (
                    <>{productImages.length} images ({numPairs} pairs = {numPairs} requests per prompt)
                      {productImages.length % 2 !== 0 && <span style={{ color: 'orange' }}> - 1 unpaired image will be ignored</span>}
                    </>
                  ) : (
                    <>{productImages.length} silo images = {numSilos} requests per prompt</>
                  )}
                </Typography>

                <Divider sx={{ my: 1 }} />

                {/* Fixed Images */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Fixed Images (optional - sent with every request)</Typography>
                  {fixedImages.length > 0 && (
                    <Button size="small" startIcon={<FaTimes size={10} />} onClick={clearFixedImages}
                      sx={{ fontSize: '0.7rem', color: 'error.main', textTransform: 'none' }}>
                      Clear All
                    </Button>
                  )}
                </Box>
                <Box {...fixedDropzone.getRootProps()} sx={{
                  border: `1px dashed ${theme.palette.divider}`, borderRadius: 1, p: 1.5,
                  textAlign: 'center', cursor: 'pointer', mb: 1, '&:hover': { borderColor: AI_COLOR },
                }}>
                  <input {...fixedDropzone.getInputProps()} />
                  <Typography variant="caption" color="text.secondary">Drop fixed images (included in every request)</Typography>
                </Box>
                {fixedImages.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                    {fixedImages.map((file, idx) => (
                      <Box key={idx} sx={{ position: 'relative', width: 48, height: 48 }}>
                        <img src={file.preview} alt={file.name}
                          onClick={() => openPreview(fixedImages.map((f, i) => ({ src: f.preview, label: f.name })), idx)}
                          style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 4, cursor: 'pointer' }} />
                        <IconButton size="small" sx={{ position: 'absolute', top: -4, right: -4, bgcolor: 'error.main', color: 'white', width: 16, height: 16 }}
                          onClick={() => removeFixedImage(idx)}><FaTimes size={6} /></IconButton>
                      </Box>
                    ))}
                  </Box>
                )}

                <Divider sx={{ my: 1 }} />

                {/* Settings */}
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Generation Settings</Typography>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  {/* Gemini image model — Nano Banana 2.1 (default, newest,
                      cheapest per image), Pro (higher fidelity) or Flash
                      (cheaper, faster). Applies to both single-image
                      generation and the Batch API path. */}
                  <Grid item xs={12}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Image Model</InputLabel>
                      <Select
                        value={imageModel}
                        label="Image Model"
                        onChange={(e) => setImageModel(e.target.value)}
                      >
                        <MenuItem value="gemini-3-pro-image">
                          Gemini 3 Pro Image — higher fidelity
                        </MenuItem>
                        <MenuItem value="gemini-3.1-flash-image">
                          Gemini 3.1 Flash Image — cheaper &amp; faster
                        </MenuItem>
                        <MenuItem value="gemini-nano-banana-2.1">
                          Gemini Nano Banana 2.1 — newest, cheapest per image (default)
                        </MenuItem>
                      </Select>
                    </FormControl>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, lineHeight: 1.5 }}>
                      <strong>Nano Banana 2.1</strong> (default) is Google's newest Flash-tier model, with better text, consistency and realism than Flash, and the cheapest per image ($0.034 / $0.050 / $0.113 at 1K / 2K / 4K) — good for large batches.{' '}
                      <strong>Pro</strong> is the pick for hero / catalog shots — best detail and prompt adherence, but slower and the most expensive ($0.134 per image at 1K/2K, $0.24 at 4K).{' '}
                      <strong>Flash</strong> trades some fidelity for speed and cost ($0.067 / $0.101 / $0.151 at 1K / 2K / 4K) — good for quick iteration and draft passes.
                    </Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Aspect Ratio</InputLabel>
                      <Select value={aspectRatio} label="Aspect Ratio" onChange={(e) => setAspectRatio(e.target.value)}>
                        {ASPECT_RATIOS.map(r => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Resolution</InputLabel>
                      <Select value={resolution} label="Resolution" onChange={(e) => setResolution(e.target.value)}>
                        {RESOLUTIONS.map(r => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Runs</InputLabel>
                      <Select value={repeatCount} label="Runs" onChange={(e) => setRepeatCount(e.target.value)}>
                        {[1, 2, 3, 4].map(n => <MenuItem key={n} value={n}>{n}x</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="caption" color="text.secondary">
                      {totalGenerations > 0
                        ? imageMode === 'pairs'
                          ? `${totalGenerations} images (${numPairs} pairs x ${selectedPromptsList.length} prompts${repeatCount > 1 ? ` x ${repeatCount} runs` : ''})`
                          : `${totalGenerations} images (${numSilos} silos x ${selectedPromptsList.length} prompts${repeatCount > 1 ? ` x ${repeatCount} runs` : ''})`
                        : 'Select prompts and images to see total'}
                    </Typography>
                  </Grid>
                </Grid>

                <TextField fullWidth size="small" multiline rows={2} label="Additional Instructions (optional)"
                  value={additionalInstructions} onChange={(e) => setAdditionalInstructions(e.target.value)} sx={{ mb: 2 }} />

                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
                  <Button variant="contained" size="large"
                    startIcon={(generating || batchSubmitting) ? <CircularProgress size={20} color="inherit" /> : <MdAutoAwesome />}
                    onClick={batchMode ? handleBatchGenerate : handleGenerate}
                    disabled={generating || batchSubmitting || selectedPromptsList.length === 0 || (imageMode === 'pairs' ? productImages.length < 2 : productImages.length < 1)}
                    sx={{ bgcolor: AI_COLOR, '&:hover': { bgcolor: TOOL_COLORS.AI.hover }, py: 1.5, fontSize: '1rem', fontWeight: 700 }}>
                    {generating
                      ? (batchMode ? `Batch Processing...` : `Generating ${progress.current}/${progress.total}...`)
                      : `Generate ${totalGenerations} Image${totalGenerations !== 1 ? 's' : ''}`}
                  </Button>
                  {/* Toggle row — kept flexWrap:nowrap so the three settings
                      stay together on a single line (Batch / Run in
                      background / Use product image name). The outer Box
                      still wraps so the toggle group can drop below the
                      Generate button on narrow widths without splitting up. */}
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'nowrap' }}>
                    <FormControlLabel
                      control={<Switch size="small" checked={batchMode} onChange={() => setBatchMode(!batchMode)}
                        sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#2DD4BF' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#2DD4BF' } }} />}
                      label={<Typography variant="caption" sx={{ fontSize: '0.75rem', color: batchMode ? '#2DD4BF' : 'text.secondary', fontWeight: batchMode ? 600 : 400, whiteSpace: 'nowrap' }}>Batch API (50% off)</Typography>}
                    />
                    <FormControlLabel
                      control={<Switch size="small" checked={backgroundMode} onChange={() => setBackgroundMode(!backgroundMode)}
                        sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: AI_COLOR }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: AI_COLOR } }} />}
                      label={<Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>Run in background</Typography>}
                    />
                    <Tooltip title={simpleNaming
                      ? 'Downloads use the product image filename. Pairs use the first (product) image only.'
                      : 'Downloads use {imageName}+{imageName2}_{timestamp}.png — descriptive, sortable, never collides.'}>
                      <FormControlLabel
                        control={<Switch size="small" checked={simpleNaming} onChange={() => setSimpleNaming(v => !v)}
                          sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: AI_COLOR }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: AI_COLOR } }} />}
                        label={<Typography variant="caption" sx={{ fontSize: '0.75rem', color: simpleNaming ? AI_COLOR : 'text.secondary', fontWeight: simpleNaming ? 600 : 400, whiteSpace: 'nowrap' }}>Use product image name</Typography>}
                      />
                    </Tooltip>
                  </Box>
                </Box>

                {/* Batch mode info */}
                {batchMode && !batchSubmitting && (
                  <Alert severity="info" sx={{ mb: 2, py: 0.5, fontSize: '0.8rem' }}>
                    Batch API processes requests asynchronously at 50% cost (capped at 1K resolution — no 2K/4K). Submit your generation, then check Batch Jobs in the History tab for status.
                  </Alert>
                )}

                {/* Batch submitting indicator */}
                {batchSubmitting && (
                  <Alert severity="warning" icon={<CircularProgress size={16} />} sx={{ mb: 2, py: 0.5, fontSize: '0.8rem' }}>
                    Submitting batch to Gemini API... Please wait.
                  </Alert>
                )}

                {/* Background mode info */}
                {backgroundMode && generating && !batchMode && (
                  <Alert severity="info" sx={{ mb: 2, py: 0.5, fontSize: '0.8rem' }}>
                    Generation running in background ({progress.current}/{progress.total}). You can switch tabs — results will appear when done.
                  </Alert>
                )}

                {/* Generated Results */}
                {generatedImages.length > 0 && (
                  <>
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Generated Images ({generatedImages.filter(r => !r.error).length}/{generatedImages.length})
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                        <Tooltip title="Grid view">
                          <IconButton size="small" onClick={() => setResultViewMode('grid')}
                            sx={{ color: resultViewMode === 'grid' ? AI_COLOR : theme.palette.text.secondary, bgcolor: resultViewMode === 'grid' ? `${AI_COLOR}15` : 'transparent' }}>
                            <FaThLarge size={14} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Compare with source images">
                          <IconButton size="small" onClick={() => setResultViewMode('comparison')}
                            sx={{ color: resultViewMode === 'comparison' ? AI_COLOR : theme.palette.text.secondary, bgcolor: resultViewMode === 'comparison' ? `${AI_COLOR}15` : 'transparent' }}>
                            <FaColumns size={14} />
                          </IconButton>
                        </Tooltip>
                        {currentBatchId && !generating && generatedImages.some(r => !r.error) && (
                          <Tooltip title="Download all as ZIP">
                            <IconButton size="small" onClick={() => downloadBatchZip(currentBatchId)}
                              sx={{ color: AI_COLOR, ml: 1 }}>
                              <FaDownload size={14} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {!generating && generatedImages.some(r => !r.error && r.retryContext) && (
                          <Button size="small"
                            variant={regenSelectMode ? 'contained' : 'outlined'}
                            startIcon={<FaRedo size={10} />}
                            onClick={() => { setRegenSelectMode(!regenSelectMode); setRegenSelected(new Set()); }}
                            sx={regenSelectMode
                              ? { fontSize: '0.7rem', bgcolor: AI_COLOR, '&:hover': { bgcolor: TOOL_COLORS.AI.hover }, textTransform: 'none', ml: 0.5 }
                              : { fontSize: '0.7rem', borderColor: AI_COLOR, color: AI_COLOR, textTransform: 'none', ml: 0.5 }
                            }>
                            {regenSelectMode ? 'Cancel Select' : 'Regenerate'}
                          </Button>
                        )}
                        {regenSelectMode && regenSelected.size > 0 && (
                          <Button size="small" variant="contained"
                            startIcon={<FaRedo size={10} />}
                            onClick={handleRegenerateSelected}
                            sx={{ fontSize: '0.7rem', bgcolor: '#2DD4BF', '&:hover': { bgcolor: '#14b8a6' }, textTransform: 'none' }}>
                            Run Again ({regenSelected.size})
                          </Button>
                        )}
                        {!generating && generatedImages.some(r => r.error && r.retryContext) && (
                          <Tooltip title="Retry failed generations">
                            <Button size="small" startIcon={<FaRedo size={10} />} onClick={handleRetryFailed}
                              sx={{ fontSize: '0.7rem', color: 'warning.main', textTransform: 'none', ml: 0.5 }}>
                              Retry Failed ({generatedImages.filter(r => r.error).length})
                            </Button>
                          </Tooltip>
                        )}
                        <Button size="small" startIcon={<FaTimes size={10} />} onClick={() => { setGeneratedImages([]); setCurrentBatchId(null); setRegenSelectMode(false); setRegenSelected(new Set()); }}
                          sx={{ fontSize: '0.7rem', color: 'error.main', textTransform: 'none', ml: 0.5 }}>
                          Clear All
                        </Button>
                      </Box>
                    </Box>

                    {resultViewMode === 'grid' ? (
                      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 2 }}>
                        {generatedImages.map((result) => {
                          const isRegenSelected = regenSelected.has(result.id);
                          return (
                          <Card key={result.id} sx={{
                            position: 'relative',
                            border: regenSelectMode && isRegenSelected ? `3px solid #2DD4BF` : regenSelectMode ? `3px solid transparent` : undefined,
                            opacity: regenSelectMode && result.error ? 0.4 : 1,
                          }}>
                            {result.error ? (
                              <CardContent>
                                <Alert severity="error" sx={{ fontSize: '0.75rem' }}>{result.error}</Alert>
                                <Typography variant="caption" color="text.secondary">{result.promptName}</Typography>
                              </CardContent>
                            ) : (
                              <>
                                {regenSelectMode && (
                                  <Checkbox size="small" checked={isRegenSelected}
                                    onClick={(e) => { e.stopPropagation(); toggleRegenSelect(result.id); }}
                                    sx={{ position: 'absolute', top: 4, left: 4, zIndex: 2, p: 0, color: 'white', '&.Mui-checked': { color: '#2DD4BF' }, bgcolor: 'rgba(0,0,0,0.5)', borderRadius: '4px', width: 26, height: 26 }} />
                                )}
                                <img src={result.image} alt={result.promptName}
                                  onClick={() => {
                                    if (regenSelectMode) { toggleRegenSelect(result.id); return; }
                                    const successResults = generatedImages.filter(r => !r.error);
                                    const idx = successResults.findIndex(r => r.id === result.id);
                                    openPreview(successResults.map(r => ({ src: r.image, label: r.promptName })), idx >= 0 ? idx : 0);
                                  }}
                                  style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', cursor: 'pointer' }} />
                                <CardContent sx={{ py: 1, px: 1.5 }}>
                                  <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>{result.promptName}</Typography>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>{result.pair}</Typography>
                                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5, mt: 0.5 }}>
                                    <Tooltip title="Save to Collection">
                                      <IconButton size="small" onClick={(e) => handleOpenSaveMenu(e, {
                                        batchId: currentBatchId,
                                        promptName: result.promptName,
                                        promptText: result.text,
                                        s3Url: result.s3Url,
                                        imageSrc: result.image,
                                        fileName: result.promptName,
                                      })}>
                                        <FaBookmark size={11} />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Download PNG">
                                      <IconButton size="small" onClick={() => downloadImage(result.image, `${getDownloadFilename(result)}.png`)}>
                                        <FaDownload size={12} />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Download JPG">
                                      <IconButton size="small" onClick={() => downloadImageAsJpg(result.image, getDownloadFilename(result))}>
                                        <FaFileImage size={12} />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Resize & Download">
                                      <IconButton size="small" sx={{ color: '#F4845F' }}
                                        onClick={() => openResizeDialog(result.image, getDownloadFilename(result))}>
                                        <FaBoxOpen size={11} />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                </CardContent>
                              </>
                            )}
                          </Card>
                          );
                        })}
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {generatedImages.map((result) => {
                          const isRegenSelected = regenSelected.has(result.id);
                          return (
                          <Card key={result.id} sx={{
                            border: regenSelectMode && isRegenSelected ? `3px solid #2DD4BF` : regenSelectMode && !result.error ? `3px solid transparent` : `1px solid ${theme.palette.divider}`,
                            opacity: regenSelectMode && result.error ? 0.4 : 1,
                            cursor: regenSelectMode && !result.error ? 'pointer' : 'default',
                          }}
                            onClick={() => { if (regenSelectMode && !result.error) toggleRegenSelect(result.id); }}>
                            {result.error ? (
                              <CardContent>
                                <Alert severity="error" sx={{ fontSize: '0.75rem' }}>{result.error}</Alert>
                                <Typography variant="caption" color="text.secondary">{result.promptName} - {result.pair}</Typography>
                              </CardContent>
                            ) : (
                              <CardContent sx={{ p: 1.5, position: 'relative' }}>
                                {regenSelectMode && (
                                  <Checkbox size="small" checked={isRegenSelected}
                                    onClick={(e) => { e.stopPropagation(); toggleRegenSelect(result.id); }}
                                    sx={{ position: 'absolute', top: 4, right: 4, zIndex: 2, p: 0, color: '#2DD4BF', '&.Mui-checked': { color: '#2DD4BF' }, bgcolor: 'rgba(0,0,0,0.4)', borderRadius: '4px', width: 26, height: 26 }} />
                                )}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{result.promptName}</Typography>
                                  <Typography variant="caption" color="text.secondary">- {result.pair}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                                  {/* Source product images */}
                                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textAlign: 'center' }}>Source</Typography>
                                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                                      {result.sourceImages && result.sourceImages.map((src, si) => (
                                        <Box key={si} sx={{ flex: 1, maxWidth: result.sourceImages.length > 1 ? '50%' : '100%' }}>
                                          <img src={src} alt={`Source ${si + 1}`}
                                            onClick={(e) => { if (regenSelectMode) { e.stopPropagation(); return; } openPreview(comparisonPreview, comparisonIndexMap[`${result.id}_src_${si}`] || 0); }}
                                            style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', borderRadius: 4, cursor: regenSelectMode ? 'pointer' : 'pointer', border: `1px solid ${theme.palette.divider}` }} />
                                        </Box>
                                      ))}
                                    </Box>
                                  </Box>

                                  {/* Arrow */}
                                  <Box sx={{ display: 'flex', alignItems: 'center', pt: 4, color: theme.palette.text.secondary, fontSize: '1.5rem' }}>
                                    →
                                  </Box>

                                  {/* Generated image */}
                                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textAlign: 'center' }}>Generated</Typography>
                                    <img src={result.image} alt={result.promptName}
                                      onClick={(e) => { if (regenSelectMode) { e.stopPropagation(); return; } openPreview(comparisonPreview, comparisonIndexMap[`${result.id}_gen`] || 0); }}
                                      style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', borderRadius: 4, cursor: 'pointer', border: `2px solid ${AI_COLOR}` }} />
                                  </Box>

                                  {/* Action buttons */}
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, pt: 3 }} onClick={(e) => e.stopPropagation()}>
                                    {result.retryContext && !generating && (
                                      <Tooltip title="Regenerate this image (same prompt, new result)">
                                        <IconButton size="small" onClick={() => {
                                          handleRegenerateSelected(new Set([result.id]));
                                        }} sx={{ color: '#2DD4BF' }}>
                                          <FaRedo size={12} />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                    <Tooltip title="Save to Collection">
                                      <IconButton size="small" onClick={(e) => handleOpenSaveMenu(e, {
                                        batchId: currentBatchId,
                                        promptName: result.promptName,
                                        promptText: result.text,
                                        s3Url: result.s3Url,
                                        imageSrc: result.image,
                                        fileName: result.promptName,
                                      })}>
                                        <FaBookmark size={11} />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Download PNG">
                                      <IconButton size="small" onClick={() => downloadImage(result.image, `${getDownloadFilename(result)}.png`)}>
                                        <FaDownload size={12} />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Download JPG">
                                      <IconButton size="small" onClick={() => downloadImageAsJpg(result.image, getDownloadFilename(result))}>
                                        <FaFileImage size={12} />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Resize & Download">
                                      <IconButton size="small" sx={{ color: '#F4845F' }}
                                        onClick={() => openResizeDialog(result.image, getDownloadFilename(result))}>
                                        <FaBoxOpen size={11} />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                </Box>
                              </CardContent>
                            )}
                          </Card>
                          );
                        })}
                      </Box>
                    )}
                  </>
                )}
              </Paper>
            </Grid>
          </Grid>
        </>
      )}

      {/* Background generation floating indicator */}
      {backgroundMode && generating && mainTab !== 'generator' && (
        <Paper elevation={3} onClick={() => setMainTab('generator')}
          sx={{ position: 'fixed', bottom: 20, right: 20, zIndex: 1000, p: 1.5, display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer', borderRadius: 2, border: `2px solid ${AI_COLOR}`, bgcolor: isDarkMode ? '#1e1e2e' : '#fff' }}>
          <CircularProgress size={20} sx={{ color: AI_COLOR }} />
          <Box>
            <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', fontSize: '0.75rem' }}>Generating {progress.current}/{progress.total}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>Click to view</Typography>
          </Box>
        </Paper>
      )}

      {/* ==================== HISTORY TAB ==================== */}
      {mainTab === 'history' && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <FaHistory color={AI_COLOR} /> Generation History
          </Typography>

          {/* Batch Jobs Section */}
          {Array.isArray(batchJobs) && batchJobs.length > 0 && (() => {
            const activeCount = batchJobs.filter(j => {
              const s = String(j?.status || '').toLowerCase();
              return s === 'submitted' || s === 'processing' || s === 'downloading_results';
            }).length;
            const completedCount = batchJobs.filter(j => String(j?.status || '').toLowerCase() === 'completed').length;
            const failedCount = batchJobs.filter(j => {
              const s = String(j?.status || '').toLowerCase();
              return s === 'error' || s === 'failed';
            }).length;
            return (
            <Box sx={{ mb: 3 }}>
              {/* Collapsible header — click anywhere on it to toggle */}
              <Box
                onClick={() => setBatchJobsCollapsed(v => !v)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                  p: 1,
                  borderRadius: 1.5,
                  border: `1px solid ${theme.palette.divider}`,
                  bgcolor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
                  cursor: 'pointer',
                  userSelect: 'none',
                  mb: batchJobsCollapsed ? 0 : 1.5,
                  '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)' },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {batchJobsCollapsed ? <FaChevronRight size={11} /> : <FaChevronDown size={11} />}
                    <FaLayerGroup color="#f59e0b" size={15} />
                  </Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Batch API Jobs
                  </Typography>
                  <Chip
                    label={batchJobs.length}
                    size="small"
                    sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700, bgcolor: '#f59e0b20', color: '#f59e0b' }}
                  />
                  {activeCount > 0 && (
                    <Chip
                      icon={<CircularProgress size={10} sx={{ color: '#f59e0b !important', ml: '6px !important' }} />}
                      label={`${activeCount} in progress`}
                      size="small"
                      sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600, bgcolor: '#f59e0b20', color: '#f59e0b' }}
                    />
                  )}
                  {completedCount > 0 && (
                    <Chip
                      label={`${completedCount} completed`}
                      size="small"
                      sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600, bgcolor: '#10b98120', color: '#10b981' }}
                    />
                  )}
                  {failedCount > 0 && (
                    <Chip
                      label={`${failedCount} failed`}
                      size="small"
                      sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600, bgcolor: '#ef444420', color: '#ef4444' }}
                    />
                  )}
                </Box>
                <Button
                  size="small"
                  startIcon={<FaRedo size={11} />}
                  onClick={(e) => { e.stopPropagation(); fetchBatchJobs(); }}
                  sx={{ textTransform: 'none', fontSize: '0.7rem', color: 'text.secondary', flexShrink: 0 }}
                >
                  Refresh
                </Button>
              </Box>
              {/* Collapsible body — capped height with internal scroll */}
              {!batchJobsCollapsed && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  maxHeight: 340,
                  overflowY: 'auto',
                  pr: 0.5,
                  // Subtle scrollbar styling
                  '&::-webkit-scrollbar': { width: 6 },
                  '&::-webkit-scrollbar-thumb': { bgcolor: alpha(theme.palette.text.secondary, 0.25), borderRadius: 3 },
                }}
              >
                {batchJobs.map(function(job) {
                  if (!job || !job.id) return null;
                  const st = String(job.status || 'unknown').toLowerCase();
                  const isActive = st === 'submitted' || st === 'processing' || st === 'downloading_results';
                  const isDone = st === 'completed';
                  const isError = st === 'error' || st === 'failed';
                  const total = Number(job.totalRequests) || 0;
                  const done = Number(job.completedRequests) || 0;
                  const progress = total > 0 ? Math.min(100, (done / total) * 100) : 0;

                  const statusLabel = isDone
                    ? 'Completed'
                    : isError
                    ? 'Failed'
                    : st === 'submitted'
                    ? 'Queued'
                    : st === 'downloading_results'
                    ? 'Downloading'
                    : 'Processing';
                  const accent = isDone ? '#10b981' : isError ? '#ef4444' : '#f59e0b';

                  // Compute duration for completed jobs
                  let durationStr = '';
                  if (job.createdAt && job.completedAt) {
                    try {
                      const createdMs = new Date(String(job.createdAt).replace(' ', 'T')).getTime();
                      const completedMs = new Date(String(job.completedAt).replace(' ', 'T')).getTime();
                      const diff = Math.max(0, Math.round((completedMs - createdMs) / 1000));
                      const mins = Math.floor(diff / 60);
                      const secs = diff % 60;
                      durationStr = mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
                    } catch (e) {}
                  }

                  return (
                    <Paper
                      key={job.id}
                      elevation={0}
                      sx={{
                        border: `1px solid ${alpha(accent, 0.25)}`,
                        borderLeft: `4px solid ${accent}`,
                        borderRadius: 2,
                        p: 1.5,
                        bgcolor: isDark ? alpha(accent, 0.05) : alpha(accent, 0.03),
                        transition: 'all 0.15s ease',
                        '&:hover': { borderColor: alpha(accent, 0.5) },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                        {/* Status icon */}
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: alpha(accent, 0.15),
                            flexShrink: 0,
                            mt: 0.25,
                          }}
                        >
                          {isActive ? (
                            <CircularProgress size={18} sx={{ color: accent }} />
                          ) : isDone ? (
                            <FaCheckCircle size={18} color={accent} />
                          ) : (
                            <FaExclamationCircle size={18} color={accent} />
                          )}
                        </Box>
                        {/* Info */}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mb: 0.25 }}>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              Job #{job.id}
                            </Typography>
                            <Chip
                              label={statusLabel}
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: '0.65rem',
                                fontWeight: 700,
                                bgcolor: alpha(accent, 0.15),
                                color: accent,
                              }}
                            />
                            {job.mode && (
                              <Chip
                                label={job.mode}
                                size="small"
                                variant="outlined"
                                sx={{ height: 20, fontSize: '0.65rem' }}
                              />
                            )}
                            {job.aspectRatio && (
                              <Chip
                                label={job.aspectRatio}
                                size="small"
                                variant="outlined"
                                sx={{ height: 20, fontSize: '0.65rem' }}
                              />
                            )}
                            {job.resolution && (
                              <Chip
                                label={job.resolution}
                                size="small"
                                variant="outlined"
                                sx={{ height: 20, fontSize: '0.65rem' }}
                              />
                            )}
                          </Box>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>
                            {done}/{total} generated
                            {job.createdAt && ` · started ${job.createdAt}`}
                            {isDone && durationStr && ` · finished in ${durationStr}`}
                          </Typography>
                          {isActive && total > 0 && (
                            <LinearProgress
                              variant="determinate"
                              value={progress}
                              sx={{
                                height: 5,
                                borderRadius: 2,
                                mt: 0.75,
                                bgcolor: alpha(accent, 0.15),
                                '& .MuiLinearProgress-bar': { bgcolor: accent, borderRadius: 2 },
                              }}
                            />
                          )}
                          {isError && job.errorMessage && (
                            <Typography
                              variant="caption"
                              sx={{
                                color: '#ef4444',
                                display: 'block',
                                mt: 0.5,
                                fontSize: '0.7rem',
                                wordBreak: 'break-word',
                              }}
                            >
                              {String(job.errorMessage).substring(0, 200)}
                            </Typography>
                          )}
                        </Box>
                        {/* Jump-to-results button for completed jobs */}
                        {isDone && job.batchId && (
                          <Tooltip title="Show results below">
                            <IconButton
                              size="small"
                              onClick={() => {
                                fetchBatchImages(job.batchId);
                                setTimeout(() => {
                                  const el = document.getElementById('batch-card-' + job.batchId);
                                  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                }, 100);
                              }}
                              sx={{ color: accent, flexShrink: 0 }}
                            >
                              <FaChevronRight size={12} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </Paper>
                  );
                })}
              </Box>
              )}
            </Box>
            );
          })()}

          {historyLoading && historyBatches.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <CircularProgress sx={{ color: AI_COLOR }} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>Loading history...</Typography>
            </Box>
          ) : historyBatches.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <FaImages size={48} style={{ opacity: 0.2 }} />
              <Typography variant="h6" color="text.secondary" sx={{ mt: 2 }}>No generations yet</Typography>
              <Typography variant="body2" color="text.secondary">Generated images will appear here organized by batch.</Typography>
              <Button variant="contained" onClick={() => setMainTab('generator')} sx={{ mt: 2, bgcolor: AI_COLOR, '&:hover': { bgcolor: TOOL_COLORS.AI.hover } }}>
                Go to Generator
              </Button>
            </Box>
          ) : (
            <>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {historyBatches.map(batch => (
                  <Card key={batch.batchId} id={'batch-card-' + batch.batchId} sx={{
                    border: expandedBatch === batch.batchId ? `2px solid ${AI_COLOR}` : `1px solid ${theme.palette.divider}`,
                    transition: 'all 0.2s ease',
                    scrollMarginTop: 16,
                  }}>
                    {/* Batch header - clickable */}
                    <CardContent
                      onClick={() => fetchBatchImages(batch.batchId)}
                      sx={{ cursor: 'pointer', '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)' }, '&:last-child': { pb: 2 } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {/* Thumbnail */}
                        <Box sx={{ width: 64, height: 64, borderRadius: 1, overflow: 'hidden', flexShrink: 0, bgcolor: isDark ? 'rgba(255,255,255,0.05)' : '#f5f5f5' }}>
                          {batch.thumbnailId ? (
                            <img src={getProxiedImageUrl(batch.thumbnailId)} alt="" style={{ width: 64, height: 64, objectFit: 'cover' }}
                              onError={(e) => { e.target.style.display = 'none'; }} />
                          ) : (
                            <Box sx={{ width: 64, height: 64, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <FaImages size={20} style={{ opacity: 0.3 }} />
                            </Box>
                          )}
                        </Box>

                        {/* Info */}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              {batch.imageCount} image{batch.imageCount !== 1 ? 's' : ''}
                            </Typography>
                            <Chip label={batch.mode} size="small" sx={{ height: 20, fontSize: '0.7rem', bgcolor: `${AI_COLOR}20`, color: AI_COLOR }} />
                            {batch.isBatchApi && (
                              <Chip label="Batch API" size="small" sx={{ height: 20, fontSize: '0.7rem', bgcolor: '#f59e0b20', color: '#f59e0b', fontWeight: 600 }} />
                            )}
                            {/* Which Gemini image model produced this batch.
                                Older rows pre-dating the model column show as
                                "Unknown" so the absence is visible, not invisible. */}
                            {(() => {
                              const label = modelDisplayLabel(batch.model);
                              const color = modelDisplayColor(batch.model);
                              return (
                                <Tooltip title={batch.model || 'Model not recorded — generated before the model picker shipped'} arrow disableInteractive>
                                  <Chip
                                    label={label}
                                    size="small"
                                    sx={{ height: 20, fontSize: '0.7rem', bgcolor: `${color}20`, color, fontWeight: 600 }}
                                  />
                                </Tooltip>
                              );
                            })()}
                          </Box>
                          <Typography variant="caption" color="text.secondary" sx={{
                            display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                          }}>
                            Prompts: {batch.promptNames}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                            {batch.createdAt}
                          </Typography>
                        </Box>

                        {/* Actions */}
                        <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
                          {batch.inputImageCount > 0 && (
                            <Tooltip title="Compare inputs vs results">
                              <IconButton size="small" onClick={(e) => { e.stopPropagation(); openCompareDialog(batch); }}
                                sx={{ color: '#2DD4BF' }}>
                                <FaColumns size={14} />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Download all as ZIP">
                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); downloadBatchZip(batch.batchId); }}
                              sx={{ color: AI_COLOR }}>
                              <FaDownload size={16} />
                            </IconButton>
                          </Tooltip>
                          <IconButton size="small" sx={{ color: theme.palette.text.secondary }}>
                            {expandedBatch === batch.batchId ? <FaTimes size={14} /> : <FaBoxOpen size={14} />}
                          </IconButton>
                        </Box>
                      </Box>
                    </CardContent>

                    {/* Expanded batch images */}
                    {expandedBatch === batch.batchId && (
                      <Box sx={{ px: 2, pb: 2 }}>
                        <Divider sx={{ mb: 1 }} />
                        {/* Batch actions for input images */}
                        {batchImages.some(img => img.inputImage1Url || img.inputImage2Url) && (
                          <Box sx={{ display: 'flex', gap: 1, mb: 1.5, alignItems: 'center' }}>
                            <Chip label="Has input images for comparison" size="small" sx={{ fontSize: '0.65rem', bgcolor: alpha(AI_COLOR, 0.1), color: AI_COLOR }} />
                            <Button size="small" variant="outlined"
                              onClick={async () => {
                                const token = localStorage.getItem('jwtToken');
                                await fetch(`${API_BASE_URL}/api/AIImageGenerator/remove-input-images-batch/${batch.batchId}`, {
                                  method: 'POST', headers: { 'Authorization': `Bearer ${token}` },
                                });
                                setBatchImages(prev => prev.map(img => ({ ...img, inputImage1Url: null, inputImage2Url: null })));
                                setSnackbar({ open: true, message: 'All input images removed from this batch' });
                              }}
                              sx={{ fontSize: '0.65rem', textTransform: 'none', borderColor: 'error.main', color: 'error.main' }}>
                              Remove All Input Images
                            </Button>
                          </Box>
                        )}
                        {batchImagesLoading ? (
                          <Box sx={{ textAlign: 'center', py: 3 }}>
                            <CircularProgress size={24} sx={{ color: AI_COLOR }} />
                          </Box>
                        ) : (
                          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 1.5 }}>
                            {batchImages.map((img, idx) => (
                              <Card key={img.id} sx={{ border: `1px solid ${theme.palette.divider}` }}>
                                <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, mb: 0.5 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.65rem', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                      {img.promptName}
                                    </Typography>
                                    <Tooltip title="Save to Collection">
                                      <IconButton size="small" sx={{ p: 0.25 }} onClick={(e) => handleOpenSaveMenu(e, {
                                        batchId: batch.batchId, promptName: img.promptName, promptText: img.promptText || '',
                                        s3Url: img.s3Url || null, imageSrc: getProxiedImageUrl(img.id), fileName: img.fileName || img.promptName,
                                      })}><FaBookmark size={8} /></IconButton>
                                    </Tooltip>
                                    <Tooltip title="Download">
                                      <IconButton size="small" sx={{ p: 0.25 }} onClick={() => downloadSingleFromHistory(img.id, img.fileName)}>
                                        <FaDownload size={9} />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Copy prompt">
                                      <IconButton size="small" sx={{ p: 0.25 }} onClick={() => {
                                        navigator.clipboard.writeText(img.promptText || '');
                                        setSnackbar({ open: true, message: 'Prompt copied!' });
                                      }}>
                                        <FaCopy size={9} />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                  <Box sx={{ position: 'relative', width: '100%', pt: '100%', borderRadius: 1, border: `2px solid ${AI_COLOR}`, overflow: 'hidden', cursor: 'pointer' }}
                                    onClick={() => openPreview(batchImages.map(i => ({ src: getProxiedImageUrl(i.id), label: i.promptName })), idx)}>
                                    <img src={getProxiedImageUrl(img.id)} alt={img.promptName}
                                      loading="lazy" decoding="async"
                                      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                                      onError={(e) => { e.target.alt = 'Failed to load'; }} />
                                  </Box>
                                  <Box sx={{ mt: 0.5, display: 'flex', justifyContent: 'center' }}>
                                    <Button size="small" onClick={() => setExpandedPromptId(prev => prev === img.id ? null : img.id)}
                                      sx={{ fontSize: '0.55rem', textTransform: 'none', color: AI_COLOR, minWidth: 0, py: 0 }}>
                                      {expandedPromptId === img.id ? '▲ Hide prompt' : '▼ Show prompt'}
                                    </Button>
                                  </Box>
                                  {expandedPromptId === img.id && (
                                    <Box sx={{ mt: 0.5, p: 0.75, bgcolor: isDark ? 'rgba(255,255,255,0.05)' : '#f5f5f5', borderRadius: 1, maxHeight: 120, overflow: 'auto' }}>
                                      <Typography variant="caption" sx={{ fontSize: '0.6rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                        {img.promptText || 'No prompt text available'}
                                      </Typography>
                                    </Box>
                                  )}
                                </CardContent>
                              </Card>
                            ))}
                          </Box>
                        )}
                      </Box>
                    )}
                  </Card>
                ))}
              </Box>

              {/* Pagination */}
              {historyTotalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  <Pagination count={historyTotalPages} page={historyPage}
                    onChange={(e, p) => fetchHistory(p)}
                    sx={{ '& .Mui-selected': { bgcolor: `${AI_COLOR} !important`, color: 'white' } }} />
                </Box>
              )}
            </>
          )}
        </Paper>
      )}

      {/* ==================== COLLECTIONS TAB ==================== */}
      {mainTab === 'collections' && (
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, minHeight: 500 }}>
          {/* Left Sidebar - Collection List
              On xs / sm we stack vertically (sidebar on top), full-width, and cap the
              list height so it doesn't push the images off-screen. Desktop keeps the
              original 250px column on the left. */}
          <Paper sx={{
            width: { xs: '100%', md: 250 },
            flexShrink: 0,
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            maxHeight: { xs: 360, md: 'none' },
          }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1, fontSize: '1rem' }}>
              <MdFolderSpecial color={AI_COLOR} size={20} /> Collections
            </Typography>

            {/* Create Collection */}
            {showCreateCollection ? (
              <Box sx={{ mb: 2 }}>
                <TextField size="small" fullWidth placeholder="Collection name..." value={newCollectionName}
                  onChange={(e) => setNewCollectionName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') createCollection(newCollectionName); if (e.key === 'Escape') { setShowCreateCollection(false); setNewCollectionName(''); } }}
                  autoFocus sx={{ mb: 1 }} />
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <Button size="small" variant="contained" onClick={() => createCollection(newCollectionName)}
                    disabled={!newCollectionName.trim()}
                    sx={{ flex: 1, bgcolor: AI_COLOR, '&:hover': { bgcolor: TOOL_COLORS.AI.hover }, fontSize: '0.75rem', textTransform: 'none' }}>
                    Save
                  </Button>
                  <Button size="small" variant="outlined" onClick={() => { setShowCreateCollection(false); setNewCollectionName(''); }}
                    sx={{ fontSize: '0.75rem', textTransform: 'none', borderColor: AI_COLOR, color: theme.palette.text.primary }}>
                    Cancel
                  </Button>
                </Box>
              </Box>
            ) : (
              <Button size="small" startIcon={<FaPlus size={10} />} variant="contained" fullWidth
                onClick={() => setShowCreateCollection(true)}
                sx={{ mb: 2, bgcolor: AI_COLOR, '&:hover': { bgcolor: TOOL_COLORS.AI.hover }, textTransform: 'none', fontSize: '0.8rem' }}>
                Create Collection
              </Button>
            )}

            {/* Collection list */}
            <Box sx={{ flex: 1, overflow: 'auto' }}>
              {collections.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4, px: 1 }}>
                  <FaFolder size={32} style={{ opacity: 0.15 }} />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    No collections yet
                  </Typography>
                </Box>
              ) : (
                collections.map(col => (
                  <Card key={col.id}
                    onClick={() => {
                      setSelectedCollection(col.id);
                      setCollectionCompareMode(false);
                      setCollectionCompareSelected(new Set());
                    }}
                    sx={{
                      mb: 1, cursor: 'pointer',
                      border: selectedCollection === col.id ? `2px solid ${AI_COLOR}` : `1px solid ${theme.palette.divider}`,
                      bgcolor: selectedCollection === col.id ? (isDark ? `${AI_COLOR}15` : `${AI_COLOR}08`) : 'transparent',
                      transition: 'all 0.15s ease', '&:hover': { borderColor: AI_COLOR },
                    }}>
                    <CardContent sx={{ py: 1.5, px: 1.5, '&:last-child': { pb: 1.5 } }}>
                      {editingCollectionId === col.id ? (
                        <Box onClick={(e) => e.stopPropagation()}>
                          <TextField size="small" fullWidth value={editingCollectionName}
                            onChange={(e) => setEditingCollectionName(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') renameCollection(col.id, editingCollectionName); if (e.key === 'Escape') { setEditingCollectionId(null); setEditingCollectionName(''); } }}
                            autoFocus sx={{ mb: 0.5 }} />
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Button size="small" onClick={() => renameCollection(col.id, editingCollectionName)}
                              sx={{ fontSize: '0.65rem', textTransform: 'none', color: AI_COLOR }}>Save</Button>
                            <Button size="small" onClick={() => { setEditingCollectionId(null); setEditingCollectionName(''); }}
                              sx={{ fontSize: '0.65rem', textTransform: 'none' }}>Cancel</Button>
                          </Box>
                        </Box>
                      ) : (
                        <>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {selectedCollection === col.id ? <FaFolderOpen size={14} color={AI_COLOR} /> : <FaFolder size={14} style={{ opacity: 0.5 }} />}
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.8rem', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {col.name}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.5 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                              {col.images.length} image{col.images.length !== 1 ? 's' : ''}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 0.25 }} onClick={(e) => e.stopPropagation()}>
                              <Tooltip title="Rename">
                                <IconButton size="small" onClick={() => { setEditingCollectionId(col.id); setEditingCollectionName(col.name); }}
                                  sx={{ width: 20, height: 20 }}>
                                  <FaEdit size={9} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <IconButton size="small" onClick={() => confirmDeleteCollection(col.id)}
                                  sx={{ width: 20, height: 20, color: 'error.main' }}>
                                  <FaTrash size={9} />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Box>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', display: 'block', mt: 0.25 }}>
                            {new Date(col.createdAt).toLocaleDateString()}
                          </Typography>
                        </>
                      )}
                    </CardContent>
                  </Card>
                ))
              )}
            </Box>
          </Paper>

          {/* Right Area - Selected Collection Images */}
          <Paper sx={{ flex: 1, p: 2, display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
            {!currentCollection ? (
              <Box sx={{ textAlign: 'center', py: 8, flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <MdFolderSpecial size={64} style={{ opacity: 0.15, color: AI_COLOR }} />
                <Typography variant="h6" color="text.secondary" sx={{ mt: 2 }}>No collections yet</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, maxWidth: 400 }}>
                  Save your best AI images here for easy access. Create a collection from the sidebar, then use the bookmark icon on generated images to save them.
                </Typography>
                <Button variant="contained" startIcon={<FaPlus size={12} />}
                  onClick={() => setShowCreateCollection(true)}
                  sx={{ mt: 3, bgcolor: AI_COLOR, '&:hover': { bgcolor: TOOL_COLORS.AI.hover }, textTransform: 'none' }}>
                  Create Your First Collection
                </Button>
              </Box>
            ) : (
              <>
                {/* Collection header */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2, gap: 1, flexWrap: 'wrap' }}>
                  <Box sx={{ minWidth: 0, flex: '1 1 240px' }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1, overflow: 'hidden' }}>
                      <FaFolderOpen color={AI_COLOR} style={{ flexShrink: 0 }} />
                      <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {currentCollection.name}
                      </Box>
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {currentCollection.images.length} image{currentCollection.images.length !== 1 ? 's' : ''} — Created {new Date(currentCollection.createdAt).toLocaleDateString()}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Button size="small" variant="outlined"
                      startIcon={<MdAddCircleOutline size={14} />}
                      onClick={openAddImagesDialog}
                      sx={{ textTransform: 'none', fontSize: '0.8rem', borderColor: AI_COLOR, color: theme.palette.text.primary }}>
                      Add Images
                    </Button>
                    {currentCollection.images.length >= 2 && (
                      <Button size="small"
                        variant={collectionCompareMode ? 'contained' : 'outlined'}
                        startIcon={<MdCompare size={14} />}
                        onClick={() => {
                          setCollectionCompareMode(!collectionCompareMode);
                          setCollectionCompareSelected(new Set());
                        }}
                        sx={{
                          textTransform: 'none', fontSize: '0.8rem',
                          ...(collectionCompareMode
                            ? { bgcolor: AI_COLOR, '&:hover': { bgcolor: TOOL_COLORS.AI.hover } }
                            : { borderColor: AI_COLOR, color: theme.palette.text.primary }),
                        }}>
                        {collectionCompareMode ? 'Exit Compare' : 'Compare'}
                      </Button>
                    )}
                    {collectionCompareMode && collectionCompareSelected.size >= 2 && (
                      <Button size="small" variant="contained"
                        onClick={() => setComparisonDialogOpen(true)}
                        sx={{ bgcolor: AI_COLOR, '&:hover': { bgcolor: TOOL_COLORS.AI.hover }, textTransform: 'none', fontSize: '0.8rem' }}>
                        Compare Selected ({collectionCompareSelected.size})
                      </Button>
                    )}
                  </Box>
                </Box>

                {collectionCompareMode && (
                  <Alert severity="info" sx={{ mb: 2, py: 0.5, fontSize: '0.8rem' }}>
                    Select 2-4 images to compare side by side. Click images to select them.
                  </Alert>
                )}

                {/* Collection images grid */}
                {currentCollection.images.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 8, flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                    <FaImages size={48} style={{ opacity: 0.15 }} />
                    <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>This collection is empty</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      Use the bookmark icon on generated or history images to add them here.
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: 'repeat(auto-fill, minmax(140px, 1fr))',
                      sm: 'repeat(auto-fill, minmax(160px, 1fr))',
                      md: 'repeat(auto-fill, minmax(180px, 1fr))',
                    },
                    gap: 1.5,
                  }}>
                    {currentCollection.images.map((colImg, idx) => {
                      const isSelected = collectionCompareSelected.has(idx);
                      const canSelect = collectionCompareMode && (isSelected || collectionCompareSelected.size < 4);
                      return (
                        <Card key={colImg.id || idx} sx={{
                          border: isSelected ? `3px solid ${AI_COLOR}` : `1px solid ${theme.palette.divider}`,
                          position: 'relative',
                          transition: 'all 0.15s ease',
                          opacity: collectionCompareMode && !canSelect && !isSelected ? 0.5 : 1,
                          cursor: collectionCompareMode ? (canSelect ? 'pointer' : 'not-allowed') : 'default',
                          '&:hover': collectionCompareMode ? {} : { borderColor: AI_COLOR },
                        }}
                          onClick={() => {
                            if (!collectionCompareMode) return;
                            if (isSelected) {
                              setCollectionCompareSelected(prev => { const next = new Set(prev); next.delete(idx); return next; });
                            } else if (canSelect) {
                              setCollectionCompareSelected(prev => new Set([...prev, idx]));
                            }
                          }}>
                          {/* Compare mode checkbox overlay */}
                          {collectionCompareMode && (
                            <Box sx={{ position: 'absolute', top: 4, left: 4, zIndex: 2 }}>
                              <Checkbox size="small" checked={isSelected}
                                disabled={!canSelect && !isSelected}
                                sx={{ p: 0, color: 'white', '&.Mui-checked': { color: AI_COLOR }, bgcolor: 'rgba(0,0,0,0.4)', borderRadius: '4px', width: 24, height: 24 }}
                                onClick={(e) => e.stopPropagation()}
                                onChange={() => {
                                  if (isSelected) {
                                    setCollectionCompareSelected(prev => { const next = new Set(prev); next.delete(idx); return next; });
                                  } else if (canSelect) {
                                    setCollectionCompareSelected(prev => new Set([...prev, idx]));
                                  }
                                }} />
                            </Box>
                          )}
                          {isSelected && (
                            <Box sx={{ position: 'absolute', top: 4, right: 4, zIndex: 2, bgcolor: AI_COLOR, color: 'white', width: 22, height: 22, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 700 }}>
                              {[...collectionCompareSelected].sort((a, b) => a - b).indexOf(idx) + 1}
                            </Box>
                          )}
                          <img src={getCollectionImageSrc(colImg)} alt={colImg.promptName}
                            onClick={(e) => {
                              if (collectionCompareMode) return;
                              e.stopPropagation();
                              openPreview(
                                currentCollection.images.map(i => ({ src: getCollectionImageSrc(i), label: i.fileName || i.promptName })),
                                idx
                              );
                            }}
                            style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', cursor: collectionCompareMode ? 'inherit' : 'pointer' }}
                            onError={(e) => { e.target.alt = 'Failed to load'; }} />
                          <CardContent sx={{ py: 0.75, px: 1, '&:last-child': { pb: 0.75 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.65rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                                {colImg.fileName || colImg.promptName || 'Untitled'}
                              </Typography>
                              {!collectionCompareMode && (
                                <Tooltip title="Remove from collection">
                                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); removeFromCollection(currentCollection.id, idx); }}
                                    sx={{ width: 20, height: 20, color: 'error.main' }}>
                                    <FaTimes size={9} />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </Box>
                )}
              </>
            )}
          </Paper>
        </Box>
      )}

      {/* ==================== SAVE TO COLLECTION MENU ==================== */}
      <Menu anchorEl={saveMenuAnchor} open={Boolean(saveMenuAnchor)} onClose={handleCloseSaveMenu}
        PaperProps={{ sx: { minWidth: 220, maxHeight: 350 } }}>
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: AI_COLOR, textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: 1 }}>
            Save to Collection
          </Typography>
        </Box>
        <Divider />
        {collections.length === 0 ? (
          <Box sx={{ px: 2, py: 2, textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">No collections yet</Typography>
          </Box>
        ) : (
          collections.map(col => (
            <MenuItem key={col.id} onClick={() => { if (saveMenuImageData) addToCollection(col.id, saveMenuImageData); }}
              sx={{ fontSize: '0.85rem' }}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <FaFolder size={14} color={AI_COLOR} />
              </ListItemIcon>
              <ListItemText primary={col.name} secondary={`${col.images.length} images`}
                primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 500 }}
                secondaryTypographyProps={{ fontSize: '0.65rem' }} />
            </MenuItem>
          ))
        )}
        <Divider />
        <MenuItem onClick={() => {
          handleCloseSaveMenu();
          setMainTab('collections');
          setShowCreateCollection(true);
        }} sx={{ fontSize: '0.85rem', color: AI_COLOR }}>
          <ListItemIcon sx={{ minWidth: 32 }}>
            <MdAddCircleOutline size={16} color={AI_COLOR} />
          </ListItemIcon>
          <ListItemText primary="Create New Collection" primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 600 }} />
        </MenuItem>
      </Menu>

      {/* Resize Dialog */}
      <Dialog open={resizeDialogOpen} onClose={() => setResizeDialogOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '2px solid #F4845F', pb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FaBoxOpen color="#F4845F" size={18} />
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem' }}>Resize & Download</Typography>
          </Box>
          <IconButton size="small" onClick={() => setResizeDialogOpen(false)}><FaTimes size={14} /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5, pb: 3, overflowY: 'auto' }}>
          {/* Image preview + original dimensions */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2.5, mt: 1 }}>
            {resizeImage && (
              <Box sx={{ width: 80, height: 80, borderRadius: 2, overflow: 'hidden', border: '2px solid #F4845F33', flexShrink: 0 }}>
                <img src={resizeImage} alt="preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </Box>
            )}
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{resizeFilename}</Typography>
              {resizeOriginalDims.w > 0 && (
                <Typography variant="caption" color="text.secondary">Original: {resizeOriginalDims.w} × {resizeOriginalDims.h}px</Typography>
              )}
            </Box>
          </Box>

          {/* Marketplace Presets */}
          <Typography variant="caption" sx={{ fontWeight: 700, color: '#F4845F', textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: 1, mb: 1.5, display: 'block' }}>
            Marketplace Presets
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1, mb: 3 }}>
            {CROP_PRESETS.map((preset) => {
              const [pw, ph] = preset.size.split('x').map(Number);
              return (
                <Button key={preset.name} variant="outlined" size="small"
                  onClick={() => {
                    downloadWithCrop(resizeImage, `${resizeFilename}_${preset.name.replace(/\s/g, '')}_${preset.size}.jpg`, pw, ph);
                    setResizeDialogOpen(false);
                  }}
                  sx={{
                    textTransform: 'none', borderColor: '#F4845F44', color: theme.palette.text.primary,
                    display: 'flex', flexDirection: 'column', py: 1.2, borderRadius: 2,
                    '&:hover': { borderColor: '#F4845F', bgcolor: '#F4845F0A' },
                  }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>{preset.name}</Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>{preset.size}px</Typography>
                </Button>
              );
            })}
          </Box>

          <Divider sx={{ mb: 2.5 }} />

          {/* Custom Size */}
          <Typography variant="caption" sx={{ fontWeight: 700, color: '#F4845F', textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: 1, mb: 1.5, display: 'block' }}>
            Custom Size
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
            <TextField size="small" label="Width" type="number" value={customWidth}
              onChange={(e) => handleCustomWidthChange(e.target.value)}
              inputProps={{ min: 1, max: 10000 }}
              sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>×</Typography>
            <TextField size="small" label="Height" type="number" value={customHeight}
              onChange={(e) => handleCustomHeightChange(e.target.value)}
              inputProps={{ min: 1, max: 10000 }}
              sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            <Typography variant="caption" color="text.secondary">px</Typography>
          </Box>
          <FormControlLabel
            control={<Switch size="small" checked={keepAspectRatio} onChange={(e) => setKeepAspectRatio(e.target.checked)}
              sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#F4845F' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#F4845F' } }} />}
            label={<Typography variant="caption" sx={{ fontSize: '0.75rem' }}>Keep aspect ratio</Typography>}
            sx={{ mb: 1 }}
          />
          <Button variant="contained" fullWidth disabled={!customWidth || !customHeight || Number(customWidth) < 1 || Number(customHeight) < 1}
            onClick={() => {
              const w = Number(customWidth), h = Number(customHeight);
              downloadWithCrop(resizeImage, `${resizeFilename}_custom_${w}x${h}.jpg`, w, h);
              setResizeDialogOpen(false);
            }}
            sx={{ bgcolor: '#F4845F', textTransform: 'none', fontWeight: 600, borderRadius: 2, mt: 0.5, '&:hover': { bgcolor: '#E56D4A' } }}>
            Download Custom Size
          </Button>
        </DialogContent>
      </Dialog>

      {/* ==================== AI PROMPT GENERATOR DIALOG ==================== */}
      <Dialog open={aiPromptsDialogOpen} onClose={() => setAiPromptsDialogOpen(false)} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '2px solid #F59E0B', pb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MdAutoAwesome color="#F59E0B" size={20} />
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem' }}>AI Generated Prompts</Typography>
            <Chip label={`${aiGeneratedPrompts.length} prompts`} size="small" sx={{ bgcolor: 'rgba(245,158,11,0.12)', color: '#F59E0B', fontWeight: 600 }} />
          </Box>
          <IconButton size="small" onClick={() => setAiPromptsDialogOpen(false)}><FaTimes size={14} /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5, pb: 3 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            These prompts were generated by AI based on your uploaded product images. Click "Use Prompt" to add it to your prompt library, or copy the text directly.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {aiGeneratedPrompts.map((p, idx) => (
              <Paper key={idx} elevation={0} sx={{
                p: 2, border: `1px solid ${theme.palette.divider}`, borderRadius: 2,
                borderLeft: `4px solid ${idx === 0 ? '#F59E0B' : idx === 1 ? AI_COLOR : '#8B5CF6'}`,
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip label={idx === 0 ? 'Product Hero' : `Creative ${idx}`} size="small"
                      sx={{ fontSize: '0.65rem', fontWeight: 700, height: 22,
                        bgcolor: idx === 0 ? 'rgba(245,158,11,0.12)' : idx === 1 ? `${AI_COLOR}18` : 'rgba(139,92,246,0.12)',
                        color: idx === 0 ? '#F59E0B' : idx === 1 ? AI_COLOR : '#8B5CF6',
                      }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{p.name}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Tooltip title="Copy prompt text">
                      <IconButton size="small" onClick={() => { navigator.clipboard.writeText(p.text); setSnackbar({ open: true, message: 'Prompt copied!' }); }}>
                        <FaDownload size={11} />
                      </IconButton>
                    </Tooltip>
                    <Button size="small" variant="contained"
                      onClick={() => {
                        const token = localStorage.getItem('jwtToken');
                        fetch(API_ENDPOINTS.AI_PROMPTS_CREATE, {
                          method: 'POST',
                          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                          body: JSON.stringify({ name: p.name, text: p.text, mode: mode, isPublic: false }),
                        }).then(r => r.json()).then(data => {
                          if (data.success) {
                            setSnackbar({ open: true, message: `"${p.name}" saved to your prompts!` });
                            fetchPrompts(mode);
                          } else {
                            setSnackbar({ open: true, message: data.message || 'Failed to save' });
                          }
                        }).catch(() => setSnackbar({ open: true, message: 'Failed to save prompt' }));
                      }}
                      sx={{ fontSize: '0.7rem', textTransform: 'none', bgcolor: idx === 0 ? '#F59E0B' : idx === 1 ? AI_COLOR : '#8B5CF6',
                        '&:hover': { bgcolor: idx === 0 ? '#D97706' : idx === 1 ? TOOL_COLORS.AI.hover : '#7C3AED' } }}>
                      Save to Prompts
                    </Button>
                  </Box>
                </Box>
                <Typography variant="body2" sx={{ fontSize: '0.8rem', lineHeight: 1.6, color: 'text.secondary', whiteSpace: 'pre-wrap' }}>
                  {p.text}
                </Typography>
              </Paper>
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAiPromptsDialogOpen(false)} sx={{ textTransform: 'none', color: 'text.secondary' }}>Close</Button>
          <Button variant="outlined" onClick={handleGenerateAiPrompts} disabled={generatingAiPrompts}
            startIcon={generatingAiPrompts ? <CircularProgress size={14} /> : <MdAutoAwesome size={14} />}
            sx={{ textTransform: 'none', borderColor: '#F59E0B', color: '#F59E0B', '&:hover': { bgcolor: 'rgba(245,158,11,0.08)', borderColor: '#D97706' } }}>
            {generatingAiPrompts ? 'Generating...' : 'Regenerate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ==================== HISTORY COMPARE DIALOG ==================== */}
      <Dialog open={!!compareDialogBatch} onClose={() => { setCompareDialogBatch(null); setCompareIndex(0); }}
        maxWidth="lg" fullWidth fullScreen={window.innerWidth < 600}
        PaperProps={{ sx: { height: '90vh' } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `2px solid #2DD4BF`, py: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FaColumns color="#2DD4BF" size={18} />
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>Compare: Input → Result</Typography>
            {compareDialogBatch && (
              <Chip label={`${compareIndex + 1} of ${batchImages.length}`} size="small" sx={{ bgcolor: '#2DD4BF20', color: '#2DD4BF', fontWeight: 700 }} />
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {compareDialogBatch && batchInputImages[compareDialogBatch.batchId]?.length > 0 && (
              <Button size="small" variant="outlined" sx={{ textTransform: 'none', fontSize: 11, borderColor: 'error.main', color: 'error.main' }}
                onClick={() => {
                  setDeleteConfirmDialog({ open: true, type: 'allInputs', batchId: compareDialogBatch.batchId });
                }}>
                Delete All Inputs
              </Button>
            )}
            <IconButton onClick={() => { setCompareDialogBatch(null); setCompareIndex(0); }}><FaTimes size={16} /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{
          p: 0, overflowY: 'auto',
          '&::-webkit-scrollbar': { width: 10 },
          '&::-webkit-scrollbar-track': { bgcolor: isDark ? '#1e1e1e' : '#f1f1f1' },
          '&::-webkit-scrollbar-thumb': { bgcolor: isDark ? '#555' : '#bbb', borderRadius: 5, '&:hover': { bgcolor: isDark ? '#777' : '#999' } },
          scrollbarColor: isDark ? '#555 #1e1e1e' : '#bbb #f1f1f1',
        }}>
          {compareDialogBatch && !batchImagesLoading && batchImages.length > 0 && (() => {
            const allInputs = batchInputImages[compareDialogBatch.batchId] || [];
            const currentResult = batchImages[compareIndex];
            // Get inputs linked to current result (by generationId)
            const currentInputs = currentResult
              ? allInputs.filter(i => i.generationId === currentResult.id)
              : [];
            // Fallback: if no per-generation match found, show all inputs for the batch
            const displayInputs = currentInputs.length > 0 ? currentInputs : allInputs;
            // Build combined image list for lightbox
            const allImages = [
              ...displayInputs.map(i => ({ src: i.s3Url, label: `INPUT: ${i.fileName}`, type: 'input' })),
              ...batchImages.map(i => ({ src: getProxiedImageUrl(i.id), label: `RESULT: ${i.promptName}`, type: 'result' })),
            ];
            // Build a single combined preview list: for each generation, its inputs then its result
            const allPreviewImages = [];
            const previewIndexMap = {}; // maps `input-${inputId}` or `result-${imgId}` to index in allPreviewImages
            // Build legacy input entries from img.inputImage1Url/inputImage2Url for older generations
            const getLegacyInputs = (img) => {
              const legacy = [];
              if (img.inputImage1Url) legacy.push({ id: `legacy-${img.id}-1`, s3Url: img.inputImage1Url, fileName: 'Input 1', isLegacy: true });
              if (img.inputImage2Url) legacy.push({ id: `legacy-${img.id}-2`, s3Url: img.inputImage2Url, fileName: 'Input 2', isLegacy: true });
              return legacy;
            };
            const resolveInputs = (img, idx) => {
              const imgInputs = allInputs.filter(i => i.generationId === img.id);
              if (imgInputs.length > 0) return imgInputs;
              const legacy = getLegacyInputs(img);
              if (legacy.length > 0) return legacy;
              return idx === 0 ? allInputs.filter(i => !i.generationId) : [];
            };
            batchImages.forEach((img, idx) => {
              const imgDisplayInputs = resolveInputs(img, idx);
              imgDisplayInputs.forEach(inp => {
                previewIndexMap[`input-${inp.id}`] = allPreviewImages.length;
                allPreviewImages.push({ src: inp.s3Url, label: `INPUT: ${inp.fileName}` });
              });
              previewIndexMap[`result-${img.id}`] = allPreviewImages.length;
              allPreviewImages.push({ src: getProxiedImageUrl(img.id), label: `RESULT: ${img.promptName}` });
            });

            return (
              <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
                {batchImages.map((img, idx) => {
                  const imgDisplayInputs = resolveInputs(img, idx);
                  return (
                    <Paper key={img.id} elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2, overflow: 'hidden' }}>
                      <Box sx={{ px: 2, py: 1, bgcolor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)', borderBottom: `1px solid ${theme.palette.divider}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                          {img.promptName}
                        </Typography>
                        <Chip label={`${idx + 1} of ${batchImages.length}`} size="small" sx={{ height: 20, fontSize: 10 }} />
                      </Box>
                      <Box sx={{ display: 'flex', gap: 2, p: 2, alignItems: 'flex-start' }}>
                        {/* Source/Input images */}
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textAlign: 'center', color: '#2DD4BF' }}>Source</Typography>
                          {imgDisplayInputs.length > 0 ? (
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                              {imgDisplayInputs.map((input, iIdx) => (
                                <Box key={input.id} sx={{ position: 'relative', flex: 1, maxWidth: imgDisplayInputs.length > 1 ? '50%' : '100%' }}>
                                  <Box sx={{ position: 'relative', width: '100%', pt: '100%', borderRadius: 1, border: `1px solid ${theme.palette.divider}`, overflow: 'hidden', cursor: 'pointer' }}
                                    onClick={() => openPreview(allPreviewImages, previewIndexMap[`input-${input.id}`] || 0)}>
                                    <img src={input.s3Url} alt={input.fileName}
                                      loading="lazy" decoding="async"
                                      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                                  </Box>
                                  {!input.isLegacy && (
                                    <IconButton size="small"
                                      onClick={(e) => { e.stopPropagation(); handleDeleteInput(compareDialogBatch.batchId, input.id); }}
                                      sx={{ position: 'absolute', top: 2, right: 2, bgcolor: 'rgba(0,0,0,0.6)', color: '#fff', width: 20, height: 20, '&:hover': { bgcolor: 'error.main' } }}>
                                      <FaTimes size={8} />
                                    </IconButton>
                                  )}
                                </Box>
                              ))}
                            </Box>
                          ) : (
                            <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>No input images</Typography>
                          )}
                        </Box>

                        {/* Arrow */}
                        <Box sx={{ display: 'flex', alignItems: 'center', pt: 4, color: theme.palette.text.secondary, fontSize: '1.5rem' }}>→</Box>

                        {/* Generated result */}
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textAlign: 'center', color: AI_COLOR }}>Generated</Typography>
                          <Box sx={{ position: 'relative' }}>
                            <Box sx={{ position: 'relative', width: '100%', pt: '100%', borderRadius: 1, border: `2px solid ${AI_COLOR}`, overflow: 'hidden', cursor: 'pointer' }}
                              onClick={() => openPreview(allPreviewImages, previewIndexMap[`result-${img.id}`] || 0)}>
                              <img src={getProxiedImageUrl(img.id)} alt={img.promptName}
                                loading="lazy" decoding="async"
                                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                            </Box>
                            <Box sx={{ position: 'absolute', bottom: 8, right: 8, display: 'flex', gap: 0.5 }}>
                              <Tooltip title="Download PNG">
                                <IconButton size="small"
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    try {
                                      const resp = await fetch(API_ENDPOINTS.AI_IMAGE_DOWNLOAD_SINGLE(img.id), {
                                        headers: { Authorization: `Bearer ${localStorage.getItem('jwtToken')}` },
                                      });
                                      const blob = await resp.blob();
                                      const url = URL.createObjectURL(blob);
                                      const downloadBase = (img.fileName || img.promptName || 'image').replace(/\.[^.]+$/, '');
                                      const a = document.createElement('a'); a.href = url; a.download = `${downloadBase}.png`;
                                      document.body.appendChild(a); a.click(); document.body.removeChild(a);
                                      URL.revokeObjectURL(url);
                                    } catch {}
                                  }}
                                  sx={{ bgcolor: 'rgba(0,0,0,0.7)', color: '#fff', width: 30, height: 30, '&:hover': { bgcolor: AI_COLOR } }}>
                                  <FaDownload size={12} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Download JPG">
                                <IconButton size="small"
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    try {
                                      const resp = await fetch(API_ENDPOINTS.AI_IMAGE_DOWNLOAD_SINGLE(img.id), {
                                        headers: { Authorization: `Bearer ${localStorage.getItem('jwtToken')}` },
                                      });
                                      const blob = await resp.blob();
                                      const imgEl = new window.Image();
                                      imgEl.onload = () => {
                                        const canvas = document.createElement('canvas');
                                        canvas.width = imgEl.width; canvas.height = imgEl.height;
                                        const ctx = canvas.getContext('2d');
                                        ctx.fillStyle = '#FFFFFF'; ctx.fillRect(0, 0, canvas.width, canvas.height);
                                        ctx.drawImage(imgEl, 0, 0);
                                        const link = document.createElement('a');
                                        link.href = canvas.toDataURL('image/jpeg', 0.95);
                                        const downloadBaseJpg = (img.fileName || img.promptName || 'image').replace(/\.[^.]+$/, '');
                                        link.download = `${downloadBaseJpg}.jpg`;
                                        document.body.appendChild(link); link.click(); document.body.removeChild(link);
                                      };
                                      imgEl.src = URL.createObjectURL(blob);
                                    } catch {}
                                  }}
                                  sx={{ bgcolor: 'rgba(0,0,0,0.7)', color: '#fff', width: 30, height: 30, '&:hover': { bgcolor: AI_COLOR } }}>
                                  <FaFileImage size={12} />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    </Paper>
                  );
                })}
              </Box>
            );
          })()}
          {batchImagesLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
              <CircularProgress sx={{ color: AI_COLOR }} />
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmDialog?.open || false} onClose={() => setDeleteConfirmDialog(null)} maxWidth="xs">
        <DialogTitle>Delete Input Images</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete all input images for this batch? This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmDialog(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={() => {
            if (deleteConfirmDialog?.batchId) handleDeleteAllInputs(deleteConfirmDialog.batchId);
            setDeleteConfirmDialog(null);
          }}>Delete All</Button>
        </DialogActions>
      </Dialog>

      {/* ==================== COMPARISON DIALOG ==================== */}
      <Dialog open={comparisonDialogOpen} onClose={() => setComparisonDialogOpen(false)}
        maxWidth="xl" fullWidth
        fullScreen={window.innerWidth < 600}
        PaperProps={{ sx: { minHeight: '70vh' } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `2px solid ${AI_COLOR}` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MdCompare color={AI_COLOR} size={22} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>Image Comparison</Typography>
            <Chip label={`${comparisonImages.length} images`} size="small" sx={{ bgcolor: `${AI_COLOR}20`, color: AI_COLOR }} />
          </Box>
          <IconButton onClick={() => setComparisonDialogOpen(false)}>
            <FaTimes size={16} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: comparisonImages.length === 2 ? '1fr 1fr' :
              comparisonImages.length === 3 ? '1fr 1fr 1fr' :
                '1fr 1fr',
            gap: 3,
            mt: 1,
          }}>
            {comparisonImages.map((colImg, idx) => (
              <Box key={colImg.id || idx} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ position: 'relative', borderRadius: 2, overflow: 'hidden', border: `2px solid ${AI_COLOR}`, cursor: 'pointer' }}
                  onClick={() => openPreview(comparisonImages.map(i => ({ src: getCollectionImageSrc(i), label: i.fileName || i.promptName })), idx)}>
                  <img src={getCollectionImageSrc(colImg)} alt={colImg.promptName}
                    style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', display: 'block' }}
                    onError={(e) => { e.target.alt = 'Failed to load'; }} />
                  <Box sx={{ position: 'absolute', bottom: 8, right: 8, bgcolor: 'rgba(0,0,0,0.6)', color: 'white', px: 1, py: 0.25, borderRadius: 1, fontSize: '0.7rem' }}>
                    Click to zoom
                  </Box>
                </Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, textAlign: 'center' }}>
                  {colImg.promptName}
                </Typography>
                {colImg.promptText && (
                  <CollapsiblePromptText text={colImg.promptText} isDark={isDark} />
                )}
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setComparisonDialogOpen(false)} sx={{ color: theme.palette.text.secondary }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Add Images to Collection Dialog */}
      <Dialog open={addImagesDialogOpen} onClose={() => setAddImagesDialogOpen(false)} maxWidth="md" fullWidth
        PaperProps={{ sx: { bgcolor: isDark ? '#1e1e2e' : '#fff', minHeight: 500 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, borderBottom: `1px solid ${theme.palette.divider}` }}>
          <MdAddCircleOutline size={20} color={AI_COLOR} />
          Add Images to Collection
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {addImagesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress size={32} sx={{ color: AI_COLOR }} /></Box>
          ) : addImagesBatches.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography color="text.secondary">No image history found. Generate some images first.</Typography>
            </Box>
          ) : (
            <Box sx={{ p: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Click a batch to expand it, then click the + button on any image to add it to "{currentCollection?.name}".
              </Typography>
              {addImagesBatches.map((batch) => (
                <Paper key={batch.batchId} elevation={0} sx={{ mb: 1, border: `1px solid ${theme.palette.divider}`, borderRadius: 1, overflow: 'hidden' }}>
                  <Box
                    onClick={() => fetchAddImagesBatchDetail(batch.batchId)}
                    sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 1.5, cursor: 'pointer',
                      '&:hover': { bgcolor: isDark ? 'rgba(114,47,55,0.08)' : 'rgba(114,47,55,0.04)' },
                      bgcolor: addImagesExpanded === batch.batchId ? (isDark ? 'rgba(114,47,55,0.12)' : 'rgba(114,47,55,0.06)') : 'transparent',
                    }}>
                    <Box component="img" src={getProxiedImageUrl(batch.thumbnailId)}
                      sx={{ width: 48, height: 48, borderRadius: 1, objectFit: 'cover', border: `1px solid ${theme.palette.divider}` }}
                      onError={(e) => { e.target.style.display = 'none'; }} />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {batch.imageCount} image{batch.imageCount !== 1 ? 's' : ''} — {batch.mode}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {batch.promptNames?.substring(0, 60)}{batch.promptNames?.length > 60 ? '...' : ''} — {new Date(batch.createdAt).toLocaleDateString()}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: 18, color: 'text.secondary' }}>
                      {addImagesExpanded === batch.batchId ? '▲' : '▼'}
                    </Typography>
                  </Box>
                  {addImagesExpanded === batch.batchId && (
                    <Box sx={{ p: 1.5, borderTop: `1px solid ${theme.palette.divider}` }}>
                      {addImagesBatchLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} sx={{ color: AI_COLOR }} /></Box>
                      ) : (
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 1 }}>
                          {addImagesBatchImages.map((img) => {
                            const alreadyAdded = currentCollection?.images.some(ci => ci.s3Url === img.s3Url);
                            return (
                              <Card key={img.id} sx={{ position: 'relative', border: `1px solid ${alreadyAdded ? AI_COLOR : theme.palette.divider}`, opacity: alreadyAdded ? 0.75 : 1 }}>
                                <Box component="img" src={getProxiedImageUrl(img.id)}
                                  sx={{ width: '100%', aspectRatio: '1', objectFit: 'cover', display: 'block' }}
                                  onError={(e) => { e.target.src = ''; }} />
                                <Box sx={{ p: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                  <Typography variant="caption" noWrap sx={{ fontSize: 10, flex: 1 }}>{img.promptName || 'Untitled'}</Typography>
                                  {alreadyAdded ? (
                                    <Chip
                                      label="Added ✕"
                                      size="small"
                                      onClick={() => {
                                        const idx = currentCollection?.images.findIndex(ci => ci.s3Url === img.s3Url);
                                        if (idx >= 0) removeFromCollection(currentCollection.id, idx);
                                      }}
                                      sx={{ height: 18, fontSize: 9, bgcolor: AI_COLOR, color: '#fff', cursor: 'pointer',
                                        '&:hover': { bgcolor: '#a33', opacity: 1 } }}
                                    />
                                  ) : (
                                    <IconButton size="small" onClick={() => addImageFromPicker(img)}
                                      sx={{ p: 0.3, color: AI_COLOR, '&:hover': { bgcolor: `${AI_COLOR}15` } }}>
                                      <MdAddCircleOutline size={18} />
                                    </IconButton>
                                  )}
                                </Box>
                              </Card>
                            );
                          })}
                        </Box>
                      )}
                    </Box>
                  )}
                </Paper>
              ))}
              {/* Pagination */}
              {addImagesTotalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 2 }}>
                  <Button size="small" variant="outlined" disabled={addImagesPage <= 1} onClick={() => fetchAddImagesBatches(addImagesPage - 1)}
                    sx={{ textTransform: 'none', borderColor: AI_COLOR, color: isDark ? '#e0d4f0' : AI_COLOR, '&:hover': { borderColor: AI_COLOR, bgcolor: `${AI_COLOR}15` }, '&.Mui-disabled': { borderColor: isDark ? 'rgba(255,255,255,0.15)' : undefined, color: isDark ? 'rgba(255,255,255,0.3)' : undefined } }}>Previous</Button>
                  <Typography variant="body2" sx={{ lineHeight: '30px', color: isDark ? '#ccc' : 'text.primary' }}>Page {addImagesPage} of {addImagesTotalPages}</Typography>
                  <Button size="small" variant="outlined" disabled={addImagesPage >= addImagesTotalPages} onClick={() => fetchAddImagesBatches(addImagesPage + 1)}
                    sx={{ textTransform: 'none', borderColor: AI_COLOR, color: isDark ? '#e0d4f0' : AI_COLOR, '&:hover': { borderColor: AI_COLOR, bgcolor: `${AI_COLOR}15` }, '&.Mui-disabled': { borderColor: isDark ? 'rgba(255,255,255,0.15)' : undefined, color: isDark ? 'rgba(255,255,255,0.3)' : undefined } }}>Next</Button>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ borderTop: `1px solid ${theme.palette.divider}` }}>
          <Button onClick={() => setAddImagesDialogOpen(false)} sx={{ color: theme.palette.text.secondary }}>Done</Button>
        </DialogActions>
      </Dialog>

      {/* Add/Edit Prompt Dialog */}
      <Dialog open={promptDialogOpen} onClose={() => setPromptDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingPrompt ? 'Edit Prompt' : `Add New ${mode} Prompt`}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField label="Prompt Name" size="small" fullWidth value={promptForm.name}
              onChange={(e) => setPromptForm(prev => ({ ...prev, name: e.target.value }))}
              placeholder="e.g., Modern Living Room Scene" />
            <TextField label="Full Prompt Text" multiline rows={10} fullWidth value={promptForm.text}
              onChange={(e) => setPromptForm(prev => ({ ...prev, text: e.target.value }))}
              placeholder="Describe the image you want to generate..." />
            <FormControlLabel
              control={
                <Switch checked={promptForm.isPublic}
                  onChange={(e) => setPromptForm(prev => ({ ...prev, isPublic: e.target.checked }))}
                  sx={{ '& .Mui-checked': { color: AI_COLOR }, '& .Mui-checked + .MuiSwitch-track': { bgcolor: AI_COLOR } }} />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {promptForm.isPublic ? <FaGlobe size={14} /> : <FaLock size={14} />}
                  <Typography variant="body2">
                    {promptForm.isPublic ? 'Shared (visible to all users)' : 'Private (only visible to you)'}
                  </Typography>
                </Box>
              }
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPromptDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSavePrompt}
            sx={{ bgcolor: AI_COLOR, '&:hover': { bgcolor: TOOL_COLORS.AI.hover } }}>
            {editingPrompt ? 'Save Changes' : 'Add Prompt'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Preview Lightbox */}
      <Dialog open={previewOpen} onClose={() => { setPreviewOpen(false); setZoomActive(false); }} maxWidth="lg"
        PaperProps={{ sx: { bgcolor: 'transparent', boxShadow: 'none', overflow: 'visible' } }}
        slotProps={{ backdrop: { sx: { bgcolor: 'rgba(0,0,0,0.85)' } } }}>
        <Box
          onTouchStart={handlePreviewTouchStart}
          onTouchEnd={handlePreviewTouchEnd}
          sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <IconButton onClick={() => setPreviewOpen(false)}
            sx={{ position: 'absolute', top: -40, right: 0, color: 'white', zIndex: 1 }}>
            <FaTimes size={20} />
          </IconButton>

          {previewImages.length > 1 && (
            <IconButton
              onClick={() => setPreviewIndex(prev => (prev - 1 + previewImages.length) % previewImages.length)}
              sx={{ position: 'fixed', left: { xs: 8, md: 'calc(50% - 45vw)' }, top: '50%', transform: 'translateY(-50%)', color: 'white', bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }, zIndex: 9999 }}>
              <FaChevronLeft size={24} />
            </IconButton>
          )}

          {previewImages[previewIndex] && (
            <Box sx={{ textAlign: 'center' }}>
              <Box
                sx={{ position: 'relative', display: 'inline-block', cursor: zoomActive ? 'none' : 'zoom-in', overflow: 'hidden', borderRadius: 1 }}
                onClick={(e) => {
                  if (!zoomActive) { setZoomActive(true); e.stopPropagation(); }
                  else setZoomActive(false);
                }}
                onMouseMove={(e) => {
                  if (!zoomActive) return;
                  const rect = e.currentTarget.getBoundingClientRect();
                  const x = ((e.clientX - rect.left) / rect.width) * 100;
                  const y = ((e.clientY - rect.top) / rect.height) * 100;
                  const overlay = e.currentTarget.querySelector('.zoom-overlay');
                  if (overlay) {
                    overlay.style.opacity = '1';
                    overlay.style.transformOrigin = `${x}% ${y}%`;
                  }
                }}
                onMouseLeave={() => {
                  // Don't reset zoom - keep showing the edge like Wayfair
                }}
              >
                <img src={previewImages[previewIndex].src} alt={previewImages[previewIndex].label}
                  style={{ maxWidth: '80vw', maxHeight: '80vh', objectFit: 'contain', borderRadius: 4, display: 'block' }} />
                {zoomActive && (
                  <div className="zoom-overlay" style={{
                    position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                    backgroundImage: `url(${previewImages[previewIndex].src})`,
                    backgroundSize: '250%', backgroundRepeat: 'no-repeat',
                    backgroundPosition: '50% 50%',
                    opacity: 0, pointerEvents: 'none',
                    transition: 'opacity 0.1s',
                  }}
                  ref={(el) => {
                    if (!el) return;
                    const parent = el.parentElement;
                    const handler = (e) => {
                      const rect = parent.getBoundingClientRect();
                      const x = ((e.clientX - rect.left) / rect.width) * 100;
                      const y = ((e.clientY - rect.top) / rect.height) * 100;
                      el.style.opacity = '1';
                      el.style.backgroundPosition = `${x}% ${y}%`;
                    };
                    parent.onmousemove = handler;
                  }}
                  />
                )}
                {!zoomActive && (
                  <div style={{
                    position: 'absolute', bottom: 8, right: 8,
                    backgroundColor: 'rgba(0,0,0,0.6)', color: 'white',
                    padding: '4px 10px', borderRadius: 4, fontSize: '0.7rem',
                    pointerEvents: 'none',
                  }}>Click to zoom</div>
                )}
              </Box>
              <Typography sx={{ color: 'white', mt: 1, fontSize: '0.9rem' }}>
                {previewImages[previewIndex].label}
                {previewImages.length > 1 && ` (${previewIndex + 1}/${previewImages.length})`}
              </Typography>
            </Box>
          )}

          {previewImages.length > 1 && (
            <IconButton
              onClick={() => setPreviewIndex(prev => (prev + 1) % previewImages.length)}
              sx={{ position: 'fixed', right: { xs: 8, md: 'calc(50% - 45vw)' }, top: '50%', transform: 'translateY(-50%)', color: 'white', bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }, zIndex: 9999 }}>
              <FaChevronRight size={24} />
            </IconButton>
          )}
        </Box>
      </Dialog>

      {/* Delete Collection Confirmation */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Delete Collection</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Are you sure you want to delete <strong>"{collections.find(c => c.id === deleteConfirmId)?.name || ''}"</strong>? This will remove the collection and all its saved images. This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteConfirmOpen(false)} sx={{ textTransform: 'none', color: 'text.secondary' }}>Cancel</Button>
          <Button onClick={() => deleteCollection(deleteConfirmId)} variant="contained"
            sx={{ textTransform: 'none', bgcolor: '#d32f2f', '&:hover': { bgcolor: '#b71c1c' }, fontWeight: 600 }}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={5000}
        onClose={() => setSnackbar({ open: false, message: '' })} message={snackbar.message} />
    </Box>
  );
};

// Small helper component for collapsible prompt text in comparison dialog
const CollapsiblePromptText = ({ text, isDark }) => {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const handleCopy = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <Box sx={{ mt: 0.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Button size="small" onClick={() => setExpanded(!expanded)}
          startIcon={expanded ? <FaChevronUp size={8} /> : <FaChevronDown size={8} />}
          sx={{ fontSize: '0.7rem', textTransform: 'none', color: isDark ? '#d4b8e0' : AI_COLOR, p: 0, minWidth: 0, fontWeight: 600,
            '&:hover': { bgcolor: isDark ? 'rgba(212,184,224,0.1)' : `${AI_COLOR}10` } }}>
          {expanded ? 'Hide prompt' : 'Show prompt'}
        </Button>
        {expanded && (
          <Button size="small" onClick={handleCopy}
            sx={{ fontSize: '0.65rem', textTransform: 'none', color: copied ? '#4CAF50' : (isDark ? '#aaa' : '#777'), p: 0, minWidth: 0, ml: 1,
              '&:hover': { color: isDark ? '#d4b8e0' : AI_COLOR } }}>
            {copied ? 'Copied!' : 'Copy'}
          </Button>
        )}
      </Box>
      {expanded && (
        <Box sx={{ mt: 0.5, p: 1.5, bgcolor: isDark ? 'rgba(255,255,255,0.08)' : '#f5f5f5', borderRadius: 1, maxHeight: 200, overflow: 'auto',
          border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`, cursor: 'pointer',
          '&:hover': { borderColor: isDark ? 'rgba(212,184,224,0.3)' : `${AI_COLOR}40` } }}
          onClick={handleCopy} title="Click to copy">
          <Typography variant="caption" sx={{ fontSize: '0.75rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
            color: isDark ? '#e0e0e0' : 'text.primary' }}>
            {text}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default AIImageGenerator;
